import { Compartment } from '@codemirror/state';
import { EditorView, keymap, placeholder as cmPlaceholder } from '@codemirror/view';
import { autocompletion, acceptCompletion, completionStatus } from '@codemirror/autocomplete';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { sql } from '@codemirror/lang-sql';
import { oneDark } from '@codemirror/theme-one-dark';
import { linter, lintGutter } from '@codemirror/lint';
import { DataGrid } from './data-grid.js';
import { buildDistributionSql, profileResultColumns } from './sql-profile.js';

if (typeof window !== 'undefined') {
  window.__DataGrid = DataGrid;
}

let root = null;

const STORAGE_KEYS = {
  draft: 'helix.sqlworkbench.draft.v1',
  saved: 'helix.sqlworkbench.saved.v1',
  sourceEntities: 'helix.sqlworkbench.sourceentities.v1',
  aiPromptCollapsed: 'helix.sqlworkbench.aiprompt.collapsed.v1',
  treeExpanded: 'helix.sqlworkbench.tree.expanded.v1'
};

const SQL_KEYWORDS = [
  'SELECT',
  'FROM',
  'WHERE',
  'GROUP BY',
  'ORDER BY',
  'LIMIT',
  'JOIN',
  'LEFT JOIN',
  'RIGHT JOIN',
  'INNER JOIN',
  'WITH',
  'CASE',
  'WHEN',
  'THEN',
  'END',
  'COUNT',
  'SUM',
  'AVG',
  'MIN',
  'MAX',
  'DISTINCT',
  'HAVING',
  'UNION',
  'VALUES',
  'EXPLAIN'
];

const MONGO_KEYWORDS = [
  'db.collection.find({})',
  'db.collection.aggregate([])',
  '$match',
  '$group',
  '$sort',
  '$project',
  '$limit',
  '$lookup',
  '$unwind',
  '$facet',
  '$count',
  '$sum',
  '$avg',
  '$gt',
  '$lt',
  '$gte',
  '$lte'
];

const ELASTICSEARCH_KEYWORDS = [
  'query',
  'aggs',
  'match',
  'match_all',
  'bool',
  'filter',
  'must',
  'should',
  'must_not',
  'range',
  'term',
  'terms',
  'sort',
  'size',
  'avg',
  'sum',
  'date_histogram'
];

const PYTHON_KEYWORDS = [
  'df',
  'result',
  'groupby',
  'assign',
  'merge',
  'sort_values',
  'dropna',
  'rename',
  'head',
  'copy',
  'reset_index',
  'pd.to_datetime'
];

const FILE_SOURCE_PREFIX = 'file::';
const FILE_ENTITY_NAME = 'dataset';
const SUPPORTED_DATASET_EXTENSIONS = new Set(['csv', 'tsv', 'pq', 'parquet', 'xlsx', 'xls', 'json']);

const CONNECTION_CONFIGS = {
  postgresql: {
    label: 'PostgreSQL',
    defaultPort: '5432',
    supportsSchema: true,
    connectionStringPlaceholder: 'postgresql://user:password@host:5432/database',
    helperText: 'Use this for Postgres-compatible warehouses that accept a standard PostgreSQL connection.'
  },
  mysql: {
    label: 'MySQL',
    defaultPort: '3306',
    supportsSchema: false,
    connectionStringPlaceholder: 'mysql+pymysql://user:password@host:3306/database',
    helperText: 'Use either a DSN or host, port, database, and credentials.'
  },
  mongodb: {
    label: 'MongoDB',
    defaultPort: '27017',
    supportsSchema: false,
    connectionStringPlaceholder: 'mongodb://user:password@host:27017/database?authSource=admin',
    helperText:
      'For MongoDB, host and database are required. Username and password are optional if the server allows it.'
  },
  redshift: {
    label: 'Redshift',
    defaultPort: '5439',
    supportsSchema: true,
    connectionStringPlaceholder: 'redshift://user:password@host:5439/database',
    helperText:
      'Standard Redshift password-auth connections can be created here. Use the main app for more specialized setup.'
  }
};

const editorLanguageCompartment = new Compartment();
const editorAutocompleteCompartment = new Compartment();
const editorPlaceholderCompartment = new Compartment();
const editorLintCompartment = new Compartment();

const SQL_WRITE_KEYWORDS_RE = /^(INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|TRUNCATE|REPLACE|MERGE|UPSERT)\b/i;

const state = {
  secret: '',
  editor: null,
  currentShareId: '',
  lastTemplate: '',
  currentDraftId: '',
  activeTab: 'results',
  dataSources: [],
  entities: [],
  sourceType: 'sql',
  entityHierarchy: {},
  entityInfoByName: {},
  lastResult: null,
  visualLoaded: false,
  connectionFormOpen: false,
  snippets: [],
  editorValidation: {
    severity: 'info',
    message: 'Choose a connection or dataset to load schema-aware editor context.',
    detail: ''
  },
  chatHistory: [],
  chatOpen: false,
  chatLoading: false,
  ctxTargetEntityName: '',
  resultsGrid: null,
  resultProfiles: []
};

const elements = {};

function getStoredJson(key, fallbackValue) {
  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return fallbackValue;
    }
    return JSON.parse(rawValue);
  } catch (_error) {
    return fallbackValue;
  }
}

function setStoredJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Ignore localStorage quota errors so the workbench remains usable.
  }
}

function getSavedDrafts() {
  const savedDrafts = getStoredJson(STORAGE_KEYS.saved, []);
  return Array.isArray(savedDrafts) ? savedDrafts : [];
}

function getBootstrapSecret() {
  const bootstrapSecret =
    (window.sqlWorkbenchBootstrap && window.sqlWorkbenchBootstrap.userSecret) || '';
  return (
    (window.userData && window.userData.secret) ||
    window.localStorage.getItem('secret') ||
    bootstrapSecret ||
    ''
  );
}

function getFixtures() {
  return window.fixtures || {};
}

function apiUrl(path) {
  const fixtures = getFixtures();
  const baseUrl = (fixtures.inf_server_url || '').replace(/\/$/, '');
  return `${baseUrl}${path}`;
}

async function fetchJson(path, options = {}) {
  const response = await fetch(apiUrl(path), options);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const errorMessage = payload.error || `Request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }
  if (payload.success === false) {
    throw new Error(payload.error || 'Request failed');
  }
  return payload;
}

function sourceModeForType(sourceType) {
  const normalized = String(sourceType || '').toLowerCase();
  if (normalized === 'mongodb') {
    return 'mongodb';
  }
  if (normalized === 'elasticsearch') {
    return 'elasticsearch';
  }
  return 'sql';
}

function isFileSourceId(sourceId) {
  return String(sourceId || '').startsWith(FILE_SOURCE_PREFIX);
}

function isFileSource(source) {
  return !!(source && (source.source_kind === 'file' || isFileSourceId(source.id)));
}

function fileExtensionForName(fileName) {
  const parts = String(fileName || '').toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

function fileDisplayName(fileName) {
  const basename = String(fileName || '').split('/').pop() || '';
  const pieces = basename.split('-');
  if (pieces.length > 1 && pieces[0].length >= 8) {
    return pieces.slice(1).join('-');
  }
  return basename;
}

function fileSourceFromName(fileName) {
  const extension = fileExtensionForName(fileName);
  return {
    id: `${FILE_SOURCE_PREFIX}${fileName}`,
    name: fileDisplayName(fileName),
    source_type: extension || 'dataset',
    source_kind: 'file',
    file_name: fileName,
    host: '',
    database: ''
  };
}

function supportsDatasetFile(fileName) {
  return SUPPORTED_DATASET_EXTENSIONS.has(fileExtensionForName(fileName));
}

function availableModesForSource(source) {
  if (isFileSource(source)) {
    return [
      { value: 'sql', label: 'SQL' },
      { value: 'python', label: 'Python / pandas' }
    ];
  }

  const normalized = String((source && source.source_type) || '').toLowerCase();
  if (normalized === 'mongodb') {
    return [{ value: 'mongodb', label: 'MongoDB' }];
  }
  if (normalized === 'elasticsearch') {
    return [{ value: 'elasticsearch', label: 'Elasticsearch JSON' }];
  }
  return [{ value: 'sql', label: 'SQL' }];
}

function syncModeOptions(preferredMode = '') {
  if (!elements.modeSelect) {
    return preferredMode || 'sql';
  }

  const source = selectedSource();
  const options = availableModesForSource(source);
  const allowedModes = options.map(option => option.value);
  const currentValue = preferredMode || elements.modeSelect.value || state.sourceType || 'sql';
  const nextValue = allowedModes.includes(currentValue) ? currentValue : options[0].value;

  elements.modeSelect.innerHTML = options
    .map(option => `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`)
    .join('');
  elements.modeSelect.value = nextValue;
  return nextValue;
}

function safeEntityLabel(value) {
  return String(value || '').trim();
}

function connectionTypeConfig(sourceType) {
  return CONNECTION_CONFIGS[String(sourceType || '').toLowerCase()] || CONNECTION_CONFIGS.postgresql;
}

function currentConnectionType() {
  return elements.connectionTypeSelect ? elements.connectionTypeSelect.value || 'postgresql' : 'postgresql';
}

function currentConnectionInputMode() {
  return elements.connectionInputModeSelect
    ? elements.connectionInputModeSelect.value || 'manual'
    : 'manual';
}

function resetConnectionForm() {
  if (!elements.connectionNameInput) {
    return;
  }

  elements.connectionNameInput.value = '';
  elements.connectionTypeSelect.value = 'postgresql';
  elements.connectionInputModeSelect.value = 'manual';
  elements.connectionStringInput.value = '';
  elements.connectionHostInput.value = '';
  elements.connectionPortInput.value = '';
  elements.connectionDatabaseInput.value = '';
  elements.connectionSchemaInput.value = '';
  elements.connectionUsernameInput.value = '';
  elements.connectionPasswordInput.value = '';
  updateConnectionForm();
}

function toggleConnectionPanel(forceOpen) {
  if (!elements.connectionPanel) {
    return;
  }

  const nextOpen = typeof forceOpen === 'boolean' ? forceOpen : !state.connectionFormOpen;
  state.connectionFormOpen = nextOpen;
  elements.connectionPanel.hidden = !nextOpen;

  if (elements.openConnectionButton) {
    elements.openConnectionButton.textContent = nextOpen ? 'Hide Connection Form' : 'New Connection';
  }

  if (nextOpen) {
    updateConnectionForm();
    if (!elements.connectionNameInput.value.trim() && elements.sourceSelect.value) {
      const sourceOption = state.dataSources.find(source => source.id === elements.sourceSelect.value);
      if (sourceOption && sourceOption.name) {
        elements.connectionNameInput.placeholder = `${sourceOption.name} copy`;
      }
    }
    window.setTimeout(() => {
      elements.connectionNameInput.focus();
    }, 0);
  }
}

function updateConnectionForm() {
  if (!elements.connectionTypeSelect) {
    return;
  }

  const config = connectionTypeConfig(currentConnectionType());
  const isConnectionStringMode = currentConnectionInputMode() === 'connection_string';

  elements.connectionStringField.hidden = !isConnectionStringMode;
  elements.connectionHostField.hidden = isConnectionStringMode;
  elements.connectionPortField.hidden = isConnectionStringMode;
  elements.connectionDatabaseField.hidden = isConnectionStringMode;
  elements.connectionUsernameField.hidden = isConnectionStringMode;
  elements.connectionPasswordField.hidden = isConnectionStringMode;
  elements.connectionSchemaField.hidden = isConnectionStringMode || !config.supportsSchema;

  if (!safeEntityLabel(elements.connectionPortInput.value) && config.defaultPort) {
    elements.connectionPortInput.value = config.defaultPort;
  }

  elements.connectionPortInput.placeholder = config.defaultPort || '';
  elements.connectionStringInput.placeholder = config.connectionStringPlaceholder || '';
  elements.connectionNote.textContent = config.helperText || '';
}

function buildConnectionPayload() {
  const payload = {
    secret: state.secret,
    name: safeEntityLabel(elements.connectionNameInput.value),
    source_type: currentConnectionType(),
    username: safeEntityLabel(elements.connectionUsernameInput.value),
    password: elements.connectionPasswordInput.value || '',
    host: safeEntityLabel(elements.connectionHostInput.value),
    port: safeEntityLabel(elements.connectionPortInput.value),
    database: safeEntityLabel(elements.connectionDatabaseInput.value),
    db_schema: safeEntityLabel(elements.connectionSchemaInput.value),
    connection_string: safeEntityLabel(elements.connectionStringInput.value)
  };

  if (currentConnectionInputMode() === 'connection_string') {
    payload.username = '';
    payload.password = '';
    payload.host = '';
    payload.port = '';
    payload.database = '';
    payload.db_schema = '';
  }

  return payload;
}

function validateConnectionPayload(payload) {
  if (!payload.secret) {
    return 'Sign in again before creating a connection.';
  }
  if (!payload.name) {
    return 'Connection name is required.';
  }
  if (!payload.source_type) {
    return 'Choose a database type first.';
  }
  if (currentConnectionInputMode() === 'connection_string') {
    if (!payload.connection_string) {
      return 'Connection string is required.';
    }
    return '';
  }

  if (!payload.host) {
    return 'Host is required.';
  }
  if (!payload.database) {
    return payload.source_type === 'mongodb' ? 'Database is required for MongoDB.' : 'Database is required.';
  }
  if (payload.source_type !== 'mongodb' && !payload.username) {
    return 'Username is required for this connection type.';
  }
  return '';
}

async function createConnection() {
  const payload = buildConnectionPayload();
  const validationError = validateConnectionPayload(payload);
  if (validationError) {
    showStatus(validationError, 'error');
    return;
  }

  elements.saveConnectionButton.disabled = true;
  showStatus('Saving and validating the new connection.', 'info');

  try {
    const result = await fetchJson('/api/data-sources', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const newSourceId = result && result.data_source ? result.data_source.id : '';
    try {
      await loadDataSources(newSourceId);
    } catch (loadError) {
      showStatus(
        loadError.message || 'Connection saved, but the workbench could not refresh its schema view.',
        'error'
      );
      return;
    }
    resetConnectionForm();
    toggleConnectionPanel(false);
    showStatus('Connection saved and selected.', 'success');
  } catch (error) {
    showStatus(error.message || 'Failed to save the connection.', 'error');
  } finally {
    elements.saveConnectionButton.disabled = false;
  }
}

function classifySchemaItems(entityInfo) {
  const items = schemaItemsForEntity(entityInfo);
  const dateColumns = [];
  const numericColumns = [];
  const categoricalColumns = [];

  items.forEach(item => {
    const name = safeEntityLabel(item.name);
    const type = String(item.type || item.semantic_type || '').toLowerCase();
    if (!name) {
      return;
    }
    if (type.includes('date') || type.includes('time') || type.includes('timestamp')) {
      dateColumns.push(name);
      return;
    }
    if (
      type.includes('int') ||
      type.includes('float') ||
      type.includes('double') ||
      type.includes('decimal') ||
      type.includes('number') ||
      type.includes('numeric')
    ) {
      numericColumns.push(name);
      return;
    }
    categoricalColumns.push(name);
  });

  return {
    items,
    dateColumns,
    numericColumns,
    categoricalColumns
  };
}

function quoteSqlIdentifier(identifier, source = null) {
  const value = safeEntityLabel(identifier);
  if (!value) {
    return '';
  }
  if (!isFileSource(source) || /^[A-Za-z_][\w$]*$/.test(value)) {
    return value;
  }
  return `"${value.replace(/"/g, '""')}"`;
}

function buildSqlTemplate(entityName, entityInfo = {}, source = null) {
  const entityLabel = safeEntityLabel(entityName);
  if (!entityLabel) {
    return 'SELECT 1;';
  }

  const { items, dateColumns, numericColumns, categoricalColumns } = classifySchemaItems(entityInfo);
  const preferredColumns = [];
  if (dateColumns[0]) {
    preferredColumns.push(dateColumns[0]);
  }
  if (categoricalColumns[0] && !preferredColumns.includes(categoricalColumns[0])) {
    preferredColumns.push(categoricalColumns[0]);
  }
  if (numericColumns[0] && !preferredColumns.includes(numericColumns[0])) {
    preferredColumns.push(numericColumns[0]);
  }
  items.forEach(item => {
    const name = safeEntityLabel(item.name);
    if (name && !preferredColumns.includes(name) && preferredColumns.length < 5) {
      preferredColumns.push(name);
    }
  });

  const selectedColumns = preferredColumns.length
    ? preferredColumns.map(columnName => quoteSqlIdentifier(columnName, source)).join(',\n  ')
    : '*';
  const orderColumn = dateColumns[0] || numericColumns[0] || '';
  const orderSql = orderColumn ? `\nORDER BY ${quoteSqlIdentifier(orderColumn, source)} DESC` : '';

  return `SELECT\n  ${selectedColumns}\nFROM ${quoteSqlIdentifier(entityLabel, source)}${orderSql}\nLIMIT 100;`;
}

function buildMongoTemplate(entityName) {
  const name = safeEntityLabel(entityName);
  if (!name) {
    return 'db.collection.find({}, {_id: 0}).limit(100)';
  }
  if (/^[A-Za-z_][\w$]*$/.test(name)) {
    return `db.${name}.find({}, {_id: 0}).limit(100)`;
  }
  return `db.getCollection(${JSON.stringify(name)}).find({}, {_id: 0}).limit(100)`;
}

function mongoCollectionReference(entityName) {
  const name = safeEntityLabel(entityName);
  if (!name) {
    return 'db.collection';
  }
  if (/^[A-Za-z_][\w$]*$/.test(name)) {
    return `db.${name}`;
  }
  return `db.getCollection(${JSON.stringify(name)})`;
}

function buildElasticTemplate() {
  return JSON.stringify(
    {
      size: 100,
      query: {
        match_all: {}
      }
    },
    null,
    2
  );
}

function buildPythonTemplate(entityInfo = {}) {
  const { dateColumns, numericColumns, categoricalColumns, items } = classifySchemaItems(entityInfo);
  const selectedColumns = [];
  if (dateColumns[0]) {
    selectedColumns.push(dateColumns[0]);
  }
  if (categoricalColumns[0] && !selectedColumns.includes(categoricalColumns[0])) {
    selectedColumns.push(categoricalColumns[0]);
  }
  if (numericColumns[0] && !selectedColumns.includes(numericColumns[0])) {
    selectedColumns.push(numericColumns[0]);
  }
  items.forEach(item => {
    const name = safeEntityLabel(item.name);
    if (name && !selectedColumns.includes(name) && selectedColumns.length < 4) {
      selectedColumns.push(name);
    }
  });

  const columnList = selectedColumns.length ? JSON.stringify(selectedColumns) : '[]';
  const sortColumn = dateColumns[0] || numericColumns[0] || '';
  const sortLines = sortColumn
    ? `\n    .sort_values(${JSON.stringify(sortColumn)}, ascending=False)`
    : '';

  return `# df contains the selected uploaded dataset.\nresult = (\n  df${selectedColumns.length ? `[${columnList}]` : ''}\n    .copy()${sortLines}\n    .head(100)\n)`;
}

function buildTemplateForMode(mode, entityName, entityInfo = {}, source = null) {
  if (mode === 'python') {
    return buildPythonTemplate(entityInfo);
  }
  if (mode === 'mongodb') {
    return buildMongoTemplate(entityName);
  }
  if (mode === 'elasticsearch') {
    return buildElasticTemplate();
  }
  return buildSqlTemplate(entityName, entityInfo, source);
}

function currentMode() {
  return elements.modeSelect.value || state.sourceType || 'sql';
}

function editorText() {
  if (!state.editor) {
    return '';
  }
  return state.editor.state.doc.toString();
}

function setEditorText(nextValue, markAsTemplate = false) {
  if (!state.editor) {
    return;
  }
  const value = String(nextValue || '');
  state.editor.dispatch({
    changes: {
      from: 0,
      to: state.editor.state.doc.length,
      insert: value
    }
  });
  state.lastTemplate = markAsTemplate ? value : '';
}

function maybeApplyTemplate(templateValue) {
  const currentValue = editorText().trim();
  const lastTemplate = String(state.lastTemplate || '').trim();
  if (!currentValue || currentValue === lastTemplate) {
    setEditorText(templateValue, true);
  }
}

function titleFromState() {
  const sourceOption = state.dataSources.find(source => source.id === elements.sourceSelect.value);
  const entityName = safeEntityLabel(elements.entitySelect.value);
  const pieces = [];
  if (sourceOption && sourceOption.name) {
    pieces.push(sourceOption.name);
  }
  if (entityName) {
    pieces.push(entityName);
  }
  return pieces.length ? pieces.join(' / ') : 'Untitled workbench';
}

function selectedSource() {
  return state.dataSources.find(source => source.id === elements.sourceSelect.value) || null;
}

function selectedEntityInfo() {
  return state.entityInfoByName[elements.entitySelect.value] || {};
}

function schemaItemsForEntity(entityInfo) {
  if (!entityInfo || typeof entityInfo !== 'object') {
    return [];
  }
  const columns = Array.isArray(entityInfo.columns) ? entityInfo.columns : [];
  const fields = Array.isArray(entityInfo.fields) ? entityInfo.fields : [];
  return columns.length ? columns : fields;
}

function totalSchemaItemCount() {
  return Object.values(state.entityInfoByName).reduce((count, entityInfo) => {
    return count + schemaItemsForEntity(entityInfo).length;
  }, 0);
}

function firstSchemaItemName(entityInfo) {
  const firstItem = schemaItemsForEntity(entityInfo).find(item => safeEntityLabel(item.name));
  return firstItem ? safeEntityLabel(firstItem.name) : '';
}

function buildModeSnippetOptions(mode) {
  const source = selectedSource();
  const entityName = safeEntityLabel(elements.entitySelect.value) || (isFileSource(source) ? FILE_ENTITY_NAME : 'table_name');
  const entityInfo = selectedEntityInfo();
  const fieldName = firstSchemaItemName(entityInfo) || 'field_name';

  if (mode === 'python') {
    return [
      {
        label: 'dataframe preview',
        type: 'snippet',
        detail: 'Python',
        info: 'Return the first 100 rows of the uploaded dataset.',
        apply: buildPythonTemplate(entityInfo)
      },
      {
        label: 'group and aggregate',
        type: 'snippet',
        detail: 'Python',
        info: 'Group the uploaded dataset with pandas and return a result dataframe.',
        apply:
          `result = (\n  df.groupby(${JSON.stringify(fieldName)}, dropna=False)\n    .size()\n    .reset_index(name='row_count')\n    .sort_values('row_count', ascending=False)\n    .head(25)\n)`
      }
    ];
  }

  if (mode === 'mongodb') {
    return [
      {
        label: 'find sample',
        type: 'snippet',
        detail: 'MongoDB',
        info: 'Insert a collection find query with a row limit.',
        apply: buildMongoTemplate(entityName)
      },
      {
        label: 'aggregate pipeline',
        type: 'snippet',
        detail: 'MongoDB',
        info: 'Start a basic aggregation pipeline.',
        apply: `${mongoCollectionReference(entityName)}.aggregate([\n  {\n    $match: {}\n  },\n  {\n    $limit: 100\n  }\n])`
      }
    ];
  }

  if (mode === 'elasticsearch') {
    return [
      {
        label: 'match_all request',
        type: 'snippet',
        detail: 'Elasticsearch',
        info: 'Insert a basic Elasticsearch request body.',
        apply: buildElasticTemplate()
      },
      {
        label: 'bool filter',
        type: 'snippet',
        detail: 'Elasticsearch',
        info: 'Insert a bool filter with a range clause.',
        apply:
          `"query": {\n  "bool": {\n    "filter": [\n      {\n        "range": {\n          "${fieldName}": {\n            "gte": "now-30d/d"\n          }\n        }\n      }\n    ]\n  }\n}`
      },
      {
        label: 'terms aggregation',
        type: 'snippet',
        detail: 'Elasticsearch',
        info: 'Insert a terms aggregation block.',
        apply:
          `"aggs": {\n  "top_${fieldName}": {\n    "terms": {\n      "field": "${fieldName}",\n      "size": 10\n    }\n  }\n}`
      },
      {
        label: 'date histogram',
        type: 'snippet',
        detail: 'Elasticsearch',
        info: 'Insert a date histogram aggregation block.',
        apply:
          `"aggs": {\n  "timeline": {\n    "date_histogram": {\n      "field": "${fieldName}",\n      "calendar_interval": "day"\n    }\n  }\n}`
      }
    ];
  }

  return [
      {
        label: 'select template',
        type: 'snippet',
        detail: 'SQL',
        info: 'Insert a basic read-only SQL query.',
        apply: buildSqlTemplate(entityName, entityInfo, source)
      },
    {
      label: 'group by aggregate',
      type: 'snippet',
      detail: 'SQL',
      info: 'Insert a grouped aggregate query.',
      apply:
        `SELECT ${fieldName}, COUNT(*) AS row_count\nFROM ${entityName}\nGROUP BY ${fieldName}\nORDER BY row_count DESC\nLIMIT 25;`
    },
    {
      label: 'window rank',
      type: 'snippet',
      detail: 'SQL',
      info: 'Insert a ranking query using a window function.',
      apply:
        `SELECT *,\n  ROW_NUMBER() OVER (ORDER BY ${fieldName} DESC) AS row_rank\nFROM ${entityName}\nLIMIT 100;`
    }
  ];
}

function buildCompletionOptions() {
  const options = [];
  const seen = new Set();
  const pushOption = option => {
    const normalized = String(option && option.label ? option.label : '').trim();
    if (!normalized) {
      return;
    }
    const type = option && option.type ? option.type : 'text';
    const key = `${normalized.toLowerCase()}::${String(type)}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    options.push({
      label: normalized,
      type,
      detail: (option && option.detail) || '',
      info: (option && option.info) || '',
      apply: option && Object.prototype.hasOwnProperty.call(option, 'apply') ? option.apply : undefined,
      boost: option && typeof option.boost === 'number' ? option.boost : 0
    });
  };

  const mode = currentMode();
  const modeKeywords =
    mode === 'mongodb'
      ? MONGO_KEYWORDS
      : mode === 'elasticsearch'
        ? ELASTICSEARCH_KEYWORDS
        : mode === 'python'
          ? PYTHON_KEYWORDS
        : SQL_KEYWORDS;

  modeKeywords.forEach(keyword => {
    pushOption({
      label: keyword,
      type: 'keyword',
      detail: mode.toUpperCase(),
      apply: mode === 'elasticsearch' ? `"${keyword}"` : keyword,
      boost: 25
    });
  });
  buildModeSnippetOptions(mode).forEach(option => pushOption({ ...option, boost: 60 }));
  state.entities.forEach(entityName => pushOption({ label: entityName, type: 'variable', detail: 'entity', boost: 40 }));

  if (mode === 'sql' && state.entityHierarchy && typeof state.entityHierarchy === 'object') {
    Object.entries(state.entityHierarchy).forEach(([databaseName, schemas]) => {
      if (!schemas || typeof schemas !== 'object') {
        return;
      }
      Object.entries(schemas).forEach(([schemaName, tables]) => {
        if (!Array.isArray(tables)) {
          return;
        }
        tables.forEach(tableName => {
          pushOption({
            label: `${schemaName}.${tableName}`,
            type: 'variable',
            detail: `${databaseName} schema`,
            boost: 35
          });
          pushOption({
            label: `${databaseName}.${schemaName}.${tableName}`,
            type: 'variable',
            detail: 'qualified entity',
            boost: 30
          });
        });
      });
    });
  }

  Object.entries(state.entityInfoByName).forEach(([entityName, entityInfo]) => {
    schemaItemsForEntity(entityInfo).forEach(item => {
      const itemName = safeEntityLabel(item.name);
      if (!itemName) {
        return;
      }
      const detail = item.type || (mode === 'elasticsearch' ? 'field' : 'column');
      const isSelectedEntity = entityName === elements.entitySelect.value;
      pushOption({
        label: itemName,
        type: 'property',
        detail,
        info: `${entityName || 'entity'} · ${detail}`,
        boost: isSelectedEntity ? 55 : 28
      });
      pushOption({
        label: `${entityName}.${itemName}`,
        type: 'property',
        detail: `qualified ${detail}`,
        boost: isSelectedEntity ? 50 : 24
      });
    });
    if (entityInfo.name && entityInfo.name !== entityName) {
      pushOption({
        label: entityInfo.name,
        type: 'variable',
        detail: 'entity alias',
        boost: 26
      });
    }
  });

  return options;
}

function completionSource(context) {
  const tokenPattern = currentMode() === 'elasticsearch' ? /[\w.$"-]*/ : /[\w.$-]*/;
  const token = context.matchBefore(tokenPattern);
  if (!context.explicit && (!token || token.from === token.to)) {
    return null;
  }

  const queryText = token ? token.text.toLowerCase() : '';
  const options = buildCompletionOptions()
    .filter(option => {
      if (!queryText) {
        return true;
      }
      return (
        option.label.toLowerCase().includes(queryText) ||
        String(option.detail || '').toLowerCase().includes(queryText)
      );
    })
    .slice(0, 80);

  return {
    from: token ? token.from : context.pos,
    options,
    validFor: tokenPattern
  };
}

function languageExtensionForMode(mode) {
  if (mode === 'mongodb') {
    return javascript();
  }
  if (mode === 'elasticsearch') {
    return json();
  }
  if (mode === 'python') {
    return javascript();
  }
  return sql();
}

function updateEditorMode(mode) {
  if (!state.editor) {
    return;
  }
  state.editor.dispatch({
    effects: [
      editorLanguageCompartment.reconfigure(languageExtensionForMode(mode)),
      editorAutocompleteCompartment.reconfigure(
        autocompletion({
          override: [completionSource],
          activateOnTyping: true
        })
      ),
      editorPlaceholderCompartment.reconfigure(
        cmPlaceholder(
          `Write ${
            mode === 'sql'
              ? 'SQL'
              : mode === 'mongodb'
                ? 'MongoDB query code'
                : mode === 'python'
                  ? 'sandboxed pandas code'
                  : 'Elasticsearch JSON'
          } here`
        )
      ),
      editorLintCompartment.reconfigure(linter(lintSourceForMode(mode), { delay: 400 }))
    ]
  });
}

function formatRelativeDate(isoDate) {
  if (!isoDate) {
    return '';
  }
  const dateValue = new Date(isoDate);
  if (Number.isNaN(dateValue.getTime())) {
    return '';
  }
  return dateValue.toLocaleString();
}

function showStatus(message, type = 'info') {
  elements.status.textContent = message;
  elements.status.className = `sqlwb-status sqlwb-status--${type} is-visible`;
}

function clearStatus() {
  elements.status.textContent = '';
  elements.status.className = 'sqlwb-status sqlwb-status--info';
}

function renderSavedDrafts() {
  if (!elements.savedList) {
    return;
  }

  const savedDrafts = getSavedDrafts().sort((left, right) => {
    return String(right.updatedAt || '').localeCompare(String(left.updatedAt || ''));
  });

  if (!savedDrafts.length) {
    elements.savedList.innerHTML =
      '<div class="sqlwb-empty">Saved workbenches live in your browser for now. Save a draft to pin the current editor state.</div>';
    return;
  }

  elements.savedList.innerHTML = savedDrafts
    .map(draft => {
      const title = safeEntityLabel(draft.title) || 'Untitled workbench';
      const description = [draft.sourceName, draft.entityName, String(draft.mode || '').toUpperCase()]
        .filter(Boolean)
        .join(' · ');
      return `
        <article class="sqlwb-saved-item" data-draft-id="${draft.id}">
          <h3>${escapeHtml(title)}</h3>
          <p>${escapeHtml(description || 'Saved draft')}</p>
          <p>Updated ${escapeHtml(formatRelativeDate(draft.updatedAt))}</p>
          <div class="sqlwb-saved-item__actions">
            <button class="mdl-button mdl-js-button" data-saved-action="load" data-draft-id="${draft.id}" type="button">Load</button>
            <button class="mdl-button mdl-js-button" data-saved-action="delete" data-draft-id="${draft.id}" type="button">Delete</button>
          </div>
        </article>
      `;
    })
    .join('');
  if (window.componentHandler) {
    window.componentHandler.upgradeElements(elements.savedList.querySelectorAll('.mdl-js-button'));
  }
}

function saveDraftToStorage(markAsNamedSave = true) {
  const currentValue = editorText();
  if (!currentValue.trim()) {
    showStatus('Nothing to save yet.', 'info');
    return;
  }

  const sourceOption = state.dataSources.find(source => source.id === elements.sourceSelect.value);
  const now = new Date().toISOString();
  const draftId = state.currentDraftId || `draft-${Date.now()}`;
  const draft = {
    id: draftId,
    title: elements.titleInput.value.trim() || titleFromState(),
    sourceId: elements.sourceSelect.value || '',
    sourceName: sourceOption ? sourceOption.name : '',
    sourceType: sourceOption ? sourceOption.source_type : state.sourceType,
    entityName: elements.entitySelect.value || '',
    mode: currentMode(),
    query: currentValue,
    aiPrompt: elements.aiPrompt.value,
    visualPrompt: elements.visualPrompt.value,
    updatedAt: now
  };

  state.currentDraftId = draftId;
  setStoredJson(STORAGE_KEYS.draft, draft);

  if (markAsNamedSave) {
    const savedDrafts = getSavedDrafts().filter(item => item.id !== draftId);
    savedDrafts.unshift(draft);
    setStoredJson(STORAGE_KEYS.saved, savedDrafts.slice(0, 20));
    renderSavedDrafts();
  }
}

function restoreDraft(draft) {
  if (!draft) {
    return;
  }

  state.currentDraftId = draft.id || '';
  elements.titleInput.value = draft.title || '';
  elements.aiPrompt.value = draft.aiPrompt || '';
  elements.visualPrompt.value = draft.visualPrompt || '';

  if (draft.sourceId) {
    elements.sourceSelect.value = draft.sourceId;
  }

  const applyDraft = async () => {
    if (draft.sourceId) {
      await onSourceChange({ preferredEntityName: draft.entityName || '' });
    }
    if (draft.mode) {
      const draftMode = syncModeOptions(draft.mode);
      updateEditorMode(draftMode);
    }
    if (draft.query) {
      setEditorText(draft.query, false);
    }
    showStatus(`Loaded ${draft.title || 'saved draft'}.`, 'success');
  };

  applyDraft().catch(error => {
    showStatus(error.message || 'Failed to load saved draft.', 'error');
  });
}

function deleteSavedDraft(draftId) {
  const savedDrafts = getSavedDrafts().filter(item => item.id !== draftId);
  setStoredJson(STORAGE_KEYS.saved, savedDrafts);
  renderSavedDrafts();
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function loadSnippets(search = '') {
  try {
    const params = new URLSearchParams({ secret: state.secret });
    if (search) {
      params.set('search', search);
    }
    const response = await fetch(`/api/sql-workbench/snippets?${params}`);
    const data = await response.json().catch(() => ({}));
    if (data.success) {
      state.snippets = Array.isArray(data.snippets) ? data.snippets : [];
      renderSnippets();
    }
  } catch (_err) {
    // Snippets are best-effort; do not surface load failures.
  }
}

function renderSnippets() {
  if (!elements.savedList) {
    return;
  }

  if (!state.snippets.length) {
    elements.savedList.innerHTML =
      '<div class="sqlwb-empty">No saved snippets yet. Run a query and click Save Snippet.</div>';
    return;
  }

  const html = state.snippets
    .map(snippet => {
      const title = escapeHtml(snippet.title || snippet.query.slice(0, 50));
      const lang = escapeHtml(snippet.query_language || 'sql');
      const entity = snippet.entity_name
        ? ` &middot; ${escapeHtml(snippet.entity_name)}`
        : '';
      const queryAttr = escapeHtml(snippet.query);
      return `<div class="sqlwb-saved-item" data-snippet-id="${escapeHtml(snippet.id)}">
  <div class="sqlwb-saved-item__title">${title}</div>
  <div style="font-size:0.78em;opacity:0.65;margin:2px 0 6px;">${lang}${entity}</div>
  <div class="sqlwb-saved-item__actions">
    <button class="mdl-button mdl-js-button sqlwb-snippet-insert-btn"
            data-query="${queryAttr}"
            data-lang="${lang}"
            type="button">Insert</button>
    <button class="mdl-button mdl-js-button sqlwb-snippet-delete-btn"
            data-id="${escapeHtml(snippet.id)}"
            type="button">Delete</button>
  </div>
</div>`;
    })
    .join('');

  elements.savedList.innerHTML = html;

  if (window.componentHandler) {
    window.componentHandler.upgradeElements(elements.savedList.querySelectorAll('.mdl-js-button'));
  }
}

async function saveSnippetToServer() {
  const query = state.editor ? state.editor.state.doc.toString().trim() : '';
  if (!query) {
    showStatus('Nothing to save.', 'info');
    return;
  }

  const title = (elements.titleInput ? elements.titleInput.value.trim() : '') || query.slice(0, 60);

  try {
    const payload = {
      secret: state.secret,
      title,
      query,
      query_language: currentMode(),
      data_source_id: elements.sourceSelect ? elements.sourceSelect.value : '',
      entity_name: elements.entitySelect ? elements.entitySelect.value : ''
    };

    const response = await fetch('/api/sql-workbench/snippets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));

    if (data.success) {
      showStatus('Snippet saved.', 'success');
      await loadSnippets();
    } else {
      showStatus(data.error || 'Failed to save snippet.', 'error');
    }
  } catch (_err) {
    showStatus('Failed to save snippet.', 'error');
  }
}

async function deleteSnippet(id) {
  if (!id) {
    return;
  }
  try {
    const params = new URLSearchParams({ secret: state.secret });
    const response = await fetch(
      `/api/sql-workbench/snippets/${encodeURIComponent(id)}?${params}`,
      { method: 'DELETE' }
    );
    const data = await response.json().catch(() => ({}));
    if (data.success) {
      await loadSnippets();
    } else {
      showStatus(data.error || 'Failed to delete snippet.', 'error');
    }
  } catch (_err) {
    showStatus('Failed to delete snippet.', 'error');
  }
}

function insertSnippetIntoEditor(query, queryLanguage) {
  if (!state.editor || !query) {
    return;
  }
  if (queryLanguage && elements.modeSelect && elements.modeSelect.value !== queryLanguage) {
    elements.modeSelect.value = queryLanguage;
    elements.modeSelect.dispatchEvent(new Event('change'));
  }
  state.editor.dispatch({
    changes: { from: 0, to: state.editor.state.doc.length, insert: query }
  });
  showStatus('Snippet inserted into editor.', 'success');
}

function inferValueType(value) {
  if (value == null) {
    return 'empty';
  }
  if (Array.isArray(value)) {
    return 'array';
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? 'integer' : 'number';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return 'string';
    }
    if (/^\d{4}-\d{2}-\d{2}(?:[t\s].*)?$/i.test(trimmed) && !Number.isNaN(Date.parse(trimmed))) {
      return 'datetime';
    }
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      return 'json';
    }
    return 'string';
  }
  if (value instanceof Date) {
    return 'datetime';
  }
  if (typeof value === 'object') {
    return 'object';
  }
  return typeof value;
}

function previewValue(value) {
  if (value == null) {
    return 'NULL';
  }
  if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  }
  return String(value);
}

function summarizeResultColumns(result, maxColumns = 12) {
  if (!result || !Array.isArray(result.columns)) {
    return [];
  }
  const rows = Array.isArray(result.data) ? result.data : [];
  return result.columns.slice(0, maxColumns).map(columnName => {
    let detectedType = 'empty';
    let sample = 'NULL';
    for (let index = 0; index < rows.length; index += 1) {
      const value = rows[index][columnName];
      if (value == null || value === '') {
        continue;
      }
      detectedType = inferValueType(value);
      sample = previewValue(value).slice(0, 60);
      break;
    }
    return {
      name: columnName,
      detectedType,
      sample
    };
  });
}

function formatMetricCards(metrics) {
  return `
    <div class="sqlwb-stat-grid">
      ${metrics
        .map(metric => {
          return `
            <article class="sqlwb-stat-card">
              <span>${escapeHtml(metric.label)}</span>
              <strong>${escapeHtml(metric.value)}</strong>
              <small>${escapeHtml(metric.note || '')}</small>
            </article>
          `;
        })
        .join('')}
    </div>
  `;
}

function clearVisualOutput(message = 'Run a query first. The visuals tab uses the current result set.') {
  if (elements.visualMeta) {
    elements.visualMeta.innerHTML = '';
  }
  if (elements.visualOutput) {
    elements.visualOutput.innerHTML = `<div class="sqlwb-empty">${escapeHtml(message)}</div>`;
  }
}

function resetResultViews(message = 'Run a query first. The visuals tab uses the current result set.') {
  state.lastResult = null;
  state.resultProfiles = [];
  state.visualLoaded = false;
  renderResults(null);
  clearVisualOutput(message);
}

function stripCodeFences(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed.startsWith('```')) {
    return trimmed;
  }
  return trimmed.replace(/^```[a-z0-9_-]*\n?/i, '').replace(/\n?```$/i, '').trim();
}

function positionToLineColumn(text, position) {
  const safePosition = Math.max(0, Math.min(Number(position) || 0, String(text || '').length));
  const before = String(text || '').slice(0, safePosition);
  const lines = before.split('\n');
  return {
    line: lines.length,
    column: lines[lines.length - 1].length + 1
  };
}

function defaultEditorValidation() {
  const source = selectedSource();
  const mode = currentMode();
  const entityName = safeEntityLabel(elements.entitySelect.value);
  const fieldCount = schemaItemsForEntity(selectedEntityInfo()).length;

  if (!source) {
    return {
      severity: 'info',
      message: 'Choose a connection or dataset to load schema-aware editor context.',
      detail: 'Autocomplete and validation become more precise after a source is selected.'
    };
  }

  if (mode === 'python') {
    if (!isFileSource(source)) {
      return {
        severity: 'info',
        message: 'Sandboxed Python is available for uploaded datasets.',
        detail: 'Choose an uploaded dataset source to use the provided df and pd objects.'
      };
    }

    return {
      severity: 'success',
      message: 'Sandboxed pandas context is ready.',
      detail: fieldCount
        ? `${fieldCount} dataset fields are available on df. Assign your final table to result.`
        : 'Assign your final dataframe to result or mutate df in place.'
    };
  }

  if (mode === 'elasticsearch') {
    if (!editorText().trim()) {
      return {
        severity: 'info',
        message: 'Write an Elasticsearch request body to validate it before execution.',
        detail: entityName
          ? `Selected index: ${entityName}.`
          : 'Select an index to keep execution deterministic.'
      };
    }
    return {
      severity: 'info',
      message: 'Editor context is loaded for Elasticsearch JSON.',
      detail: fieldCount
        ? `${fieldCount} indexed field suggestions are available for the selected index.`
        : 'Field suggestions appear after schema metadata loads.'
    };
  }

  return {
    severity: 'success',
    message: `${String(mode || 'sql').toUpperCase()} editor context is ready.`,
    detail: fieldCount
      ? `${fieldCount} field suggestions are available for ${entityName || 'the selected entity'}.`
      : `${state.entities.length} entities are available for autocomplete.`
  };
}

function validateElasticsearchEditor(queryText) {
  const rawText = String(queryText || '');
  const stripped = stripCodeFences(rawText);
  if (!stripped) {
    return defaultEditorValidation();
  }

  try {
    const parsed = JSON.parse(stripped);
    if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
      return {
        severity: 'error',
        message: 'Elasticsearch JSON must be a single top-level object.',
        detail: 'Wrap the request body in `{ ... }` before running it.'
      };
    }

    if (!elements.entitySelect.value && !safeEntityLabel(parsed._index) && !safeEntityLabel(parsed.index)) {
      return {
        severity: 'warning',
        message: 'Valid JSON, but no index is selected.',
        detail: 'Pick an index in the sidebar or include `_index` in the request body.'
      };
    }

    return {
      severity: 'success',
      message: 'Valid Elasticsearch JSON request body.',
      detail: schemaItemsForEntity(selectedEntityInfo()).length
        ? 'Field-aware autocomplete is active for the selected index.'
        : 'You can run this query now.'
    };
  } catch (error) {
    const message = error && error.message ? error.message : 'Invalid JSON';
    const match = /position\s+(\d+)/i.exec(message);
    let detail = message;
    if (match) {
      const coordinates = positionToLineColumn(stripped, Number(match[1]));
      detail = `Line ${coordinates.line}, column ${coordinates.column}. ${message}`;
    }
    return {
      severity: 'error',
      message: 'Invalid Elasticsearch JSON.',
      detail
    };
  }
}

function validateEditorContent() {
  const validation =
    currentMode() === 'elasticsearch'
      ? validateElasticsearchEditor(editorText())
      : defaultEditorValidation();
  state.editorValidation = validation;
  renderEditorDiagnostics();
  return validation;
}

function renderEditorDiagnostics() {
  if (!elements.editorDiagnostics) {
    return;
  }

  const source = selectedSource();
  const entityName = safeEntityLabel(elements.entitySelect.value);
  const currentEntityInfo = selectedEntityInfo();
  const schemaItems = schemaItemsForEntity(currentEntityInfo);
  const validation = state.editorValidation || defaultEditorValidation();
  if (elements.editorContext) {
    const sourceLabel = source ? safeEntityLabel(source.name) || 'Selected source' : 'No source';
    const entityLabel = entityName || 'No table';
    elements.editorContext.innerHTML = `
      <span class="sqlwb-context-chip sqlwb-context-chip--mode">${escapeHtml(String(currentMode() || 'sql').toUpperCase())}</span>
      <span class="sqlwb-context-chip" title="${escapeHtml(sourceLabel)}">${escapeHtml(sourceLabel)}</span>
      <span class="sqlwb-context-chip" title="${escapeHtml(entityLabel)}">${escapeHtml(entityLabel)}</span>
    `;
  }
  const metrics = [
    {
      label: 'Mode',
      value: String(currentMode() || 'sql').toUpperCase(),
      note: source ? safeEntityLabel(source.source_type) || 'connection' : 'no source'
    },
    {
      label: 'Connection',
      value: source ? safeEntityLabel(source.name) || 'Selected source' : 'None',
      note: source ? safeEntityLabel(source.database || source.host || source.source_type) : 'Choose a source'
    },
    {
      label: 'Entity',
      value: entityName || 'None',
      note: entityName ? `${schemaItems.length} fields loaded` : `${state.entities.length} entities available`
    },
    {
      label: 'Autocomplete',
      value: String(totalSchemaItemCount() || buildCompletionOptions().length),
      note: totalSchemaItemCount() ? 'schema items indexed' : 'keyword and snippet suggestions'
    }
  ];

  elements.editorDiagnostics.innerHTML = `
    ${formatMetricCards(metrics)}
    <div class="sqlwb-diagnostic sqlwb-diagnostic--${escapeHtml(validation.severity || 'info')}">
      <strong>${escapeHtml(validation.message || '')}</strong>
      <span>${escapeHtml(validation.detail || '')}</span>
    </div>
  `;
}

function renderVisualMeta(payload) {
  if (!elements.visualMeta) {
    return;
  }

  const metadata = payload && payload.metadata ? payload.metadata : {};
  if (!metadata || Object.keys(metadata).length === 0) {
    elements.visualMeta.innerHTML = '';
    return;
  }

  const columnSummary = Array.isArray(metadata.column_summary) ? metadata.column_summary : [];
  const metrics = [
    {
      label: 'Chart',
      value: metadata.graph_type || payload.graph_type || 'Auto',
      note: metadata.visualization_prompt || 'Current result set'
    },
    {
      label: 'Connector',
      value: metadata.source_type || 'unknown',
      note: metadata.query_language || 'query'
    },
    {
      label: 'Entity',
      value: metadata.entity_name || 'result set',
      note: `${metadata.returned_row_count || (state.lastResult && state.lastResult.returned_row_count) || 0} rows used`
    }
  ];

  const columnChips = columnSummary.length
    ? `<div class="sqlwb-summary-chips">${columnSummary
        .slice(0, 8)
        .map(item => {
          const label = [item.name, item.semantic_type || item.dtype].filter(Boolean).join(' · ');
          return `<span class="sqlwb-chip sqlwb-chip--muted">${escapeHtml(label)}</span>`;
        })
        .join('')}</div>`
    : '';

  elements.visualMeta.innerHTML = `
    ${formatMetricCards(metrics)}
    ${columnChips}
  `;
}

function renderResults(result) {
  if (!result || !Array.isArray(result.data) || result.data.length === 0) {
    elements.resultsMeta.innerHTML =
      '<div class="sqlwb-empty sqlwb-empty--compact">No rows returned yet. Run a query to inspect connector-backed results and chart suggestions.</div>';
    if (state.resultsGrid) {
      state.resultsGrid.destroy();
      state.resultsGrid = null;
    }
    elements.resultsContent.innerHTML =
      '<div class="sqlwb-empty">The result set will render here in a row-first sheet view.</div>';
    renderProfiles(null);
    return;
  }

  const columnSummary = summarizeResultColumns(result);
  const metrics = [
    {
      label: 'Rows',
      value: String(result.row_count || 0),
      note: result.truncated ? `showing first ${result.returned_row_count}` : 'full result in memory'
    },
    {
      label: 'Columns',
      value: String(Array.isArray(result.columns) ? result.columns.length : 0),
      note: result.entity_name || 'current result set'
    },
    {
      label: 'Connector',
      value: result.source_type || 'unknown',
      note: result.query_language || 'query'
    },
    {
      label: 'Execution',
      value: result.execution_time || 'n/a',
      note: result.message ? result.message : 'read-only execution'
    }
  ];

  const summaryChips = columnSummary.length
    ? `<div class="sqlwb-summary-chips">${columnSummary
        .map(item => {
          return `<button type="button" class="sqlwb-chip sqlwb-chip--muted sqlwb-summary-profile-link" data-profile-column-target="${escapeHtml(
            item.name
          )}" title="Open the ${escapeHtml(item.name)} distribution">${escapeHtml(
            `${item.name} · ${item.detectedType}`
          )}</button>`;
        })
        .join('')}</div>`
    : '';

  elements.resultsMeta.innerHTML = `
    ${formatMetricCards(metrics)}
    ${summaryChips}
    ${result.message ? `<div class="sqlwb-inline-note">${escapeHtml(result.message)}</div>` : ''}
  `;

  if (!state.resultsGrid) {
    elements.resultsContent.innerHTML = '';
    const gridHost = document.createElement('div');
    gridHost.className = 'sqlwb-results-grid';
    elements.resultsContent.appendChild(gridHost);
    state.resultsGrid = new DataGrid(gridHost, {
      columns: result.columns,
      rows: result.data
    });
  } else {
    state.resultsGrid.setData(result.columns, result.data);
  }

  renderProfiles(result);
}

function profileMetricHtml(label, value) {
  return `
    <div class="sqlwb-profile-metric">
      <span>${escapeHtml(label)}</span>
      <strong title="${escapeHtml(value)}">${escapeHtml(value)}</strong>
    </div>
  `;
}

function profileBarsHtml(profile) {
  const bars = Array.isArray(profile.bars) ? profile.bars : [];
  if (!bars.length) {
    return '<div class="sqlwb-profile-empty">No populated values in the returned rows.</div>';
  }
  const maximum = Math.max(1, ...bars.map(item => Number(item.count) || 0));
  return `
    <div class="sqlwb-profile-bars" aria-label="${escapeHtml(profile.name)} distribution">
      ${bars
        .map(item => {
          const count = Math.max(0, Number(item.count) || 0);
          const width = Math.max(count ? 3 : 0, Math.min(100, (count / maximum) * 100));
          return `
            <div class="sqlwb-profile-bar">
              <span title="${escapeHtml(item.label)}">${escapeHtml(item.label)}</span>
              <i aria-hidden="true"><b style="width:${width.toFixed(2)}%"></b></i>
              <em>${escapeHtml(String(count))}</em>
            </div>
          `;
        })
        .join('')}
    </div>
  `;
}

function distributionSqlForProfile(profile, result) {
  const queryLanguage = String((result && result.query_language) || currentMode()).toLowerCase();
  if (queryLanguage !== 'sql') {
    return '';
  }
  const source = selectedSource();
  return buildDistributionSql({
    columnName: profile.name,
    kind: profile.kind,
    entityName: (result && result.entity_name) || elements.entitySelect.value,
    originalQuery: (result && result.workbench_query) || '',
    sourceType: (result && result.source_type) || (source && source.source_type) || ''
  });
}

function renderProfiles(result) {
  if (!elements.profileOutput || !elements.profileMeta) {
    return;
  }
  if (!result || !Array.isArray(result.data) || !result.data.length) {
    state.resultProfiles = [];
    elements.profileMeta.innerHTML =
      '<div class="sqlwb-empty sqlwb-empty--compact">Run a query to profile its returned columns.</div>';
    elements.profileOutput.innerHTML =
      '<div class="sqlwb-empty">Automatic distributions will appear here without another database query.</div>';
    return;
  }

  state.resultProfiles = profileResultColumns(result).map(profile => ({
    ...profile,
    sql: distributionSqlForProfile(profile, result)
  }));
  const returnedRows = result.data.length;
  const totalRows = Number(result.row_count) || returnedRows;
  elements.profileMeta.innerHTML = `
    <div>
      <strong>Automatic column distributions</strong>
      <span>Profiled ${escapeHtml(String(returnedRows))} returned row${returnedRows === 1 ? '' : 's'}${
        totalRows > returnedRows ? ` from ${escapeHtml(String(totalRows))} total` : ''
      }. SQL actions reproduce each profile against the current query.</span>
    </div>
  `;

  elements.profileOutput.innerHTML = state.resultProfiles
    .map((profile, index) => {
      const actionHtml = profile.sql
        ? `
          <div class="sqlwb-profile-actions">
            <button type="button" class="sqlwb-profile-button" data-profile-action="copy" data-profile-index="${index}">Copy SQL</button>
            <button type="button" class="sqlwb-profile-button sqlwb-profile-button--primary" data-profile-action="use" data-profile-index="${index}">Open in editor</button>
          </div>
        `
        : '<span class="sqlwb-profile-action-note">SQL actions are available for SQL result sets.</span>';
      const metricHtml = [
        { label: 'Populated', value: `${profile.populatedCount}/${profile.rowCount}` },
        { label: 'Distinct', value: String(profile.distinctCount) },
        { label: 'Missing', value: `${profile.missingPercent.toFixed(1)}%` },
        ...(Array.isArray(profile.metrics) ? profile.metrics : [])
      ]
        .map(item => profileMetricHtml(item.label, String(item.value)))
        .join('');
      return `
        <article class="sqlwb-profile-card" id="sqlwb-profile-${index}" data-profile-column="${escapeHtml(profile.name)}" tabindex="-1">
          <header>
            <div>
              <strong title="${escapeHtml(profile.name)}">${escapeHtml(profile.name)}</strong>
              <span>${escapeHtml(profile.kind)}</span>
            </div>
            ${actionHtml}
          </header>
          <div class="sqlwb-profile-metrics">${metricHtml}</div>
          ${profileBarsHtml(profile)}
        </article>
      `;
    })
    .join('');
}

async function copyTextToClipboard(text) {
  const value = String(text || '');
  if (!value) {
    return false;
  }
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_error) {
      // Continue to the selection-based fallback for restricted browser contexts.
    }
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  let copied = false;
  try {
    copied = Boolean(document.execCommand && document.execCommand('copy'));
  } catch (_error) {
    copied = false;
  }
  textarea.remove();
  return copied;
}

async function handleProfileAction(button) {
  const index = Number(button && button.dataset ? button.dataset.profileIndex : -1);
  const profile = Number.isInteger(index) ? state.resultProfiles[index] : null;
  if (!profile || !profile.sql) {
    showStatus('Distribution SQL is unavailable for this column.', 'error');
    return;
  }
  if (button.dataset.profileAction === 'use') {
    if (currentMode() !== 'sql') {
      elements.modeSelect.value = 'sql';
      updateEditorMode('sql');
    }
    setEditorText(profile.sql, false);
    state.lastTemplate = '';
    state.editor.focus();
    showStatus(`Opened the ${profile.name} distribution query in the editor. Undo restores the prior query.`, 'success');
    return;
  }
  const copied = await copyTextToClipboard(profile.sql);
  showStatus(
    copied ? `Copied distribution SQL for ${profile.name}.` : 'The browser blocked clipboard access.',
    copied ? 'success' : 'error'
  );
  if (copied) {
    const previousLabel = button.textContent;
    button.textContent = 'Copied';
    window.setTimeout(() => {
      if (button.isConnected) {
        button.textContent = previousLabel;
      }
    }, 1200);
  }
}

function renderSchema(entityInfo) {
  const items = schemaItemsForEntity(entityInfo);
  const sampleDocs = Array.isArray(entityInfo.sample_docs) ? entityInfo.sample_docs : [];

  if (!entityInfo || (!items.length && !sampleDocs.length)) {
    elements.schemaOutput.innerHTML =
      '<div class="sqlwb-empty">Choose a connection or dataset and entity to load detailed schema metadata.</div>';
    return;
  }

  const searchableCount = items.filter(item => item.searchable !== false).length;
  const nullableCount = items.filter(item => item.nullable).length;
  const metrics = [
    {
      label: 'Entity',
      value: entityInfo.name || elements.entitySelect.value || 'Selected entity',
      note: entityInfo.schema || entityInfo.database || 'schema metadata'
    },
    {
      label: 'Fields',
      value: String(items.length),
      note: `${searchableCount} searchable`
    },
    {
      label: 'Rows',
      value: String(
        typeof entityInfo.row_count !== 'undefined'
          ? entityInfo.row_count
          : typeof entityInfo.doc_count !== 'undefined'
            ? entityInfo.doc_count
            : sampleDocs.length
      ),
      note:
        typeof entityInfo.doc_count !== 'undefined'
          ? 'documents indexed'
          : typeof entityInfo.row_count !== 'undefined'
            ? 'rows estimated'
            : 'sample available'
    },
    {
      label: 'Nullable',
      value: String(nullableCount),
      note: `${sampleDocs.length} sample docs`
    }
  ];

  const columnsHtml = items
    .map(item => {
      const detail = item.type || (item.searchable === false ? 'not searchable' : 'field');
      const badges = [
        detail,
        item.nullable ? 'nullable' : '',
        item.searchable === false ? 'not searchable' : ''
      ]
        .filter(Boolean)
        .map(label => `<span class="sqlwb-chip sqlwb-chip--muted">${escapeHtml(label)}</span>`)
        .join('');
      return `
        <div class="sqlwb-column">
          <div class="sqlwb-column__meta">
            <strong>${escapeHtml(item.name || '')}</strong>
            <span>${escapeHtml(
              [item.description, item.format, item.indexed ? 'indexed' : ''].filter(Boolean).join(' · ') || detail
            )}</span>
          </div>
          <div class="sqlwb-column__badges">${badges}</div>
        </div>
      `;
    })
    .join('');

  const sampleHtml = sampleDocs.length
    ? `<div class="sqlwb-field"><label>Sample documents</label><textarea readonly>${escapeHtml(
        JSON.stringify(sampleDocs.slice(0, 3), null, 2)
      )}</textarea></div>`
    : '';

  elements.schemaOutput.innerHTML = `
    ${formatMetricCards(metrics)}
    <div class="sqlwb-columns">${columnsHtml || '<div class="sqlwb-empty">No columns returned for this entity.</div>'}</div>
    ${sampleHtml}
  `;
}

function renderCapabilities(payload) {
  if (!elements.capabilities) {
    return;
  }

  const plugins = payload && payload.plugins ? payload.plugins : {};
  const groups = [
    ['Data Platforms', Object.keys(plugins.data_platforms || {})],
    ['Export Targets', Object.keys(plugins.exports || {})],
    ['Charts', Object.keys(plugins.charts || {})]
  ];

  elements.capabilities.innerHTML = groups
    .map(([label, entries]) => {
      const tags = entries.length
        ? entries
            .slice(0, 24)
            .map(entry => `<span class="sqlwb-chip">${escapeHtml(entry)}</span>`)
            .join('')
        : '<span class="sqlwb-chip">No entries</span>';
      return `
        <section class="sqlwb-capability-group">
          <h3>${escapeHtml(label)}</h3>
          <p>Current registry snapshot from the graph and export pipeline.</p>
          <div class="sqlwb-capability-tags">${tags}</div>
        </section>
      `;
    })
    .join('');
}

async function loadCapabilities() {
  if (!elements.capabilities) {
    return;
  }

  try {
    const payload = await fetchJson('/api/graph/plugins');
    renderCapabilities(payload);
  } catch (error) {
    elements.capabilities.innerHTML = `<div class="sqlwb-empty">${escapeHtml(
      error.message || 'Failed to load capabilities.'
    )}</div>`;
  }
}

async function loadUploadedFileSources() {
  const payload = await fetchJson(`/api/get-files?secret=${encodeURIComponent(state.secret)}`);
  const files = Array.isArray(payload.files) ? payload.files : [];
  return files.filter(fileName => supportsDatasetFile(fileName)).map(fileName => fileSourceFromName(fileName));
}

function populateSourceSelect(preferredSourceId = '') {
  const optionsHtml = [
    '<option value="">Choose a connection or dataset</option>',
    ...state.dataSources.map(source => {
      const suffix = isFileSource(source)
        ? `uploaded ${safeEntityLabel(source.source_type) || 'dataset'}`
        : safeEntityLabel(source.source_type);
      return `<option value="${escapeHtml(source.id)}">${escapeHtml(source.name)} · ${escapeHtml(suffix)}</option>`;
    })
  ];
  elements.sourceSelect.innerHTML = optionsHtml.join('');
  if (preferredSourceId) {
    elements.sourceSelect.value = preferredSourceId;
  }
}

function rememberEntityForSource(sourceId, entityName) {
  if (!sourceId) return;
  const map = getStoredJson(STORAGE_KEYS.sourceEntities, {}) || {};
  if (entityName) {
    map[sourceId] = entityName;
  } else {
    delete map[sourceId];
  }
  setStoredJson(STORAGE_KEYS.sourceEntities, map);
}

function recallEntityForSource(sourceId) {
  if (!sourceId) return '';
  const map = getStoredJson(STORAGE_KEYS.sourceEntities, {}) || {};
  return typeof map[sourceId] === 'string' ? map[sourceId] : '';
}

function buildEntityTreeModel() {
  const entities = state.entities || [];
  const hierarchy = state.entityHierarchy || {};
  const used = new Set();
  const groups = [];

  Object.entries(hierarchy).forEach(([databaseName, schemas]) => {
    if (!schemas || typeof schemas !== 'object') return;
    const subGroups = [];
    Object.entries(schemas).forEach(([schemaName, tables]) => {
      if (!Array.isArray(tables) || !tables.length) return;
      const leaves = [];
      tables.forEach(tableName => {
        const qualified = `${schemaName}.${tableName}`;
        let value = '';
        if (entities.includes(tableName)) value = tableName;
        else if (entities.includes(qualified)) value = qualified;
        else return;
        used.add(value);
        leaves.push({ label: tableName, value });
      });
      if (leaves.length) {
        subGroups.push({ label: schemaName, leaves });
      }
    });
    if (subGroups.length) {
      groups.push({ label: databaseName, subGroups });
    }
  });

  const ungrouped = entities.filter(name => !used.has(name)).map(name => ({ label: name, value: name }));
  return { groups, ungrouped };
}

function setTreeGroupExpanded(groupKey, expanded) {
  const map = getStoredJson(STORAGE_KEYS.treeExpanded, {}) || {};
  map[groupKey] = expanded;
  setStoredJson(STORAGE_KEYS.treeExpanded, map);
}

function isTreeGroupExpanded(groupKey, fallback) {
  const map = getStoredJson(STORAGE_KEYS.treeExpanded, {}) || {};
  return typeof map[groupKey] === 'boolean' ? map[groupKey] : fallback;
}

function renderEntityTree() {
  if (!elements.entityTree) return;
  const { groups, ungrouped } = buildEntityTreeModel();
  const filterText = (elements.entityFilter && elements.entityFilter.value || '').trim().toLowerCase();
  const currentValue = elements.entitySelect ? elements.entitySelect.value : '';

  const leafHtml = (leaf, padClass = '') => {
    if (filterText && !leaf.label.toLowerCase().includes(filterText) && !leaf.value.toLowerCase().includes(filterText)) {
      return '';
    }
    const active = leaf.value === currentValue ? ' is-active' : '';
    return `<button type="button" class="sqlwb-tree-leaf${padClass ? ' ' + padClass : ''}${active}" role="treeitem" data-entity-value="${escapeHtml(leaf.value)}" title="${escapeHtml(leaf.value)}">${escapeHtml(leaf.label)}</button>`;
  };

  const parts = [];
  groups.forEach(group => {
    const subParts = [];
    group.subGroups.forEach(sub => {
      const leaves = sub.leaves.map(leaf => leafHtml(leaf)).filter(Boolean).join('');
      if (!leaves) return;
      const subKey = `${group.label}::${sub.label}`;
      const forceOpen = !!filterText;
      const isOpen = forceOpen || isTreeGroupExpanded(subKey, true);
      subParts.push(
        `<details class="sqlwb-tree-group sqlwb-tree-group--sub" data-tree-key="${escapeHtml(subKey)}"${isOpen ? ' open' : ''}>` +
          `<summary>${escapeHtml(sub.label)}</summary>` +
          `<div class="sqlwb-tree-leaves">${leaves}</div>` +
        `</details>`
      );
    });
    if (!subParts.length) return;
    const groupKey = `db::${group.label}`;
    const forceOpen = !!filterText;
    const isOpen = forceOpen || isTreeGroupExpanded(groupKey, true);
    parts.push(
      `<details class="sqlwb-tree-group" data-tree-key="${escapeHtml(groupKey)}"${isOpen ? ' open' : ''}>` +
        `<summary>${escapeHtml(group.label)}</summary>` +
        subParts.join('') +
      `</details>`
    );
  });

  const ungroupedLeaves = ungrouped.map(leaf => leafHtml(leaf, 'sqlwb-tree-leaf--flat')).filter(Boolean).join('');
  if (ungroupedLeaves) {
    if (parts.length) {
      parts.push(
        `<details class="sqlwb-tree-group" data-tree-key="other::flat" open>` +
          `<summary>Other</summary>` +
          `<div class="sqlwb-tree-leaves">${ungroupedLeaves}</div>` +
        `</details>`
      );
    } else {
      parts.push(`<div class="sqlwb-tree-leaves">${ungroupedLeaves}</div>`);
    }
  }

  elements.entityTree.innerHTML = parts.length
    ? parts.join('')
    : `<div class="sqlwb-tree-empty">${filterText ? 'No tables match that filter.' : 'Pick a connection to see tables.'}</div>`;
}

function selectEntityFromTree(entityName) {
  if (!elements.entitySelect || !entityName) return;
  if (elements.entitySelect.value === entityName) {
    renderEntityTree();
    return;
  }
  elements.entitySelect.value = entityName;
  elements.entitySelect.dispatchEvent(new Event('change', { bubbles: true }));
  renderEntityTree();
}

function setAllTreeGroupsExpanded(expanded) {
  if (!elements.entityTree) return;
  elements.entityTree.querySelectorAll('.sqlwb-tree-group').forEach(details => {
    details.open = expanded;
    const key = details.dataset.treeKey;
    if (key) setTreeGroupExpanded(key, expanded);
  });
}

function applyAiRowCollapsed(collapsed) {
  if (!elements.aiRow || !elements.aiToggle) return;
  elements.aiRow.hidden = !!collapsed;
  elements.aiToggle.setAttribute('aria-expanded', String(!collapsed));
}

function restoreAiRowState() {
  if (!elements.aiRow || !elements.aiToggle) return;
  const collapsed = getStoredJson(STORAGE_KEYS.aiPromptCollapsed, true);
  applyAiRowCollapsed(collapsed !== false);
}

function populateEntitySelect(preferredEntityName = '') {
  const entities = state.entities || [];
  const hierarchy = state.entityHierarchy || {};
  const groupedEntities = new Set();
  const groupHtml = [];

  Object.entries(hierarchy).forEach(([databaseName, schemas]) => {
    if (!schemas || typeof schemas !== 'object') {
      return;
    }
    Object.entries(schemas).forEach(([schemaName, tables]) => {
      if (!Array.isArray(tables) || !tables.length) {
        return;
      }
      const label = [databaseName, schemaName].filter(Boolean).join('.');
      const opts = tables
        .filter(tableName => entities.includes(tableName) || entities.includes(`${schemaName}.${tableName}`))
        .map(tableName => {
          const value = entities.includes(tableName) ? tableName : `${schemaName}.${tableName}`;
          groupedEntities.add(value);
          return `<option value="${escapeHtml(value)}">${escapeHtml(tableName)}</option>`;
        });
      if (opts.length) {
        groupHtml.push(`<optgroup label="${escapeHtml(label)}">${opts.join('')}</optgroup>`);
      }
    });
  });

  const leftovers = entities.filter(entityName => !groupedEntities.has(entityName));
  const leftoverHtml = leftovers
    .map(entityName => `<option value="${escapeHtml(entityName)}">${escapeHtml(entityName)}</option>`)
    .join('');
  const tailHtml = groupHtml.length && leftoverHtml
    ? `<optgroup label="Other">${leftoverHtml}</optgroup>`
    : leftoverHtml;

  elements.entitySelect.innerHTML = [
    '<option value="">Choose a table, collection, index, or dataset</option>',
    ...groupHtml,
    tailHtml
  ].join('');

  if (preferredEntityName && entities.includes(preferredEntityName)) {
    elements.entitySelect.value = preferredEntityName;
  } else if (entities.length) {
    elements.entitySelect.value = entities[0];
  } else {
    elements.entitySelect.value = '';
  }
  renderEntityTree();
}

async function loadEntitySchema(entityName) {
  if (!elements.sourceSelect.value || !entityName) {
    renderSchema({});
    validateEditorContent();
    return;
  }

  try {
    const params = new URLSearchParams({
      secret: state.secret,
      data_source_id: elements.sourceSelect.value,
      entity_name: entityName,
      include_stats: '1'
    });
    const payload = await fetchJson(`/api/sql-workbench/schema?${params.toString()}`);
    const entityInfo = payload.entity_info || {};
    state.entityInfoByName[entityName] = entityInfo;
    renderSchema(entityInfo);
    updateEditorMode(currentMode());
    validateEditorContent();
  } catch (error) {
    renderSchema({});
    validateEditorContent();
    showStatus(error.message || 'Failed to load schema metadata.', 'error');
  }
}

async function onSourceChange({ preferredEntityName = '' } = {}) {
  const sourceId = elements.sourceSelect.value;
  if (!sourceId) {
    state.entities = [];
    state.entityHierarchy = {};
    state.entityInfoByName = {};
    state.sourceType = 'sql';
    populateEntitySelect('');
    renderSchema({});
    resetResultViews();
    validateEditorContent();
    return;
  }

  const recalledEntity = preferredEntityName || recallEntityForSource(sourceId);

  const selectedSource = state.dataSources.find(source => source.id === sourceId);
  state.sourceType = sourceModeForType(selectedSource ? selectedSource.source_type : 'sql');
  const nextMode = syncModeOptions(state.sourceType);
  state.sourceType = nextMode;
  updateEditorMode(nextMode);

  showStatus('Loading schema metadata for the selected source.', 'info');
  const params = new URLSearchParams({
    secret: state.secret,
    data_source_id: sourceId
  });
  const payload = await fetchJson(`/api/sql-workbench/schema?${params.toString()}`);
  state.entities = Array.isArray(payload.entities) ? payload.entities : [];
  state.entityHierarchy = payload && payload.hierarchy && typeof payload.hierarchy === 'object' ? payload.hierarchy : {};
  state.entityInfoByName = {};
  resetResultViews();
  populateEntitySelect(recalledEntity);

  const entityName = elements.entitySelect.value;
  if (entityName) {
    await loadEntitySchema(entityName);
  } else {
    renderSchema({});
  }

  const nextTemplate = buildTemplateForMode(currentMode(), entityName, selectedEntityInfo(), selectedSource);
  maybeApplyTemplate(nextTemplate);
  validateEditorContent();
  clearStatus();
}

async function loadDataSources(preferredSourceId = '', preferredEntityName = '') {
  const params = new URLSearchParams({
    secret: state.secret
  });
  const [payload, uploadedFiles] = await Promise.all([
    fetchJson(`/api/data-sources?${params.toString()}`),
    loadUploadedFileSources().catch(() => [])
  ]);
  const connectionSources = Array.isArray(payload.data_sources) ? payload.data_sources : [];
  state.dataSources = [...connectionSources, ...uploadedFiles];
  populateSourceSelect(preferredSourceId);

  if (!state.dataSources.length) {
    state.entities = [];
    state.entityHierarchy = {};
    state.entityInfoByName = {};
    populateEntitySelect('');
    renderSchema({});
    resetResultViews();
    validateEditorContent();
    showStatus('No connections or uploaded datasets found yet. Create a connection or upload data to start.', 'info');
    toggleConnectionPanel(true);
    return;
  }

  if (!elements.sourceSelect.value) {
    elements.sourceSelect.value = state.dataSources[0].id;
  }
  if (state.connectionFormOpen && !preferredSourceId) {
    toggleConnectionPanel(false);
  }
  await onSourceChange({ preferredEntityName });
}

async function runQuery() {
  if (!elements.sourceSelect.value) {
    showStatus('Choose a connection or dataset before running a query.', 'error');
    return;
  }

  const query = editorText();
  if (!query.trim()) {
    showStatus('Query is empty.', 'error');
    return;
  }

  const validation = validateEditorContent();
  if (currentMode() === 'elasticsearch' && validation.severity === 'error') {
    showStatus(validation.message, 'error');
    return;
  }

  showStatus('Running query against the selected source.', 'info');
  elements.runButton.disabled = true;

  // Reset the visuals-ready badge for the new query run.
  const _runQueryPageRoot = root || (typeof document !== 'undefined' ? document.getElementById('sql-workbench-page') : null);
  if (_runQueryPageRoot) {
    const _visualTab = _runQueryPageRoot.querySelector('[data-workbench-tab="visuals"]');
    if (_visualTab) {
      delete _visualTab.dataset.visualReady;
    }
  }

  try {
    const payload = await fetchJson('/api/sql-workbench/execute', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        secret: state.secret,
        data_source_id: elements.sourceSelect.value,
        query,
        query_language: currentMode(),
        entity_name: elements.entitySelect.value,
        max_rows: 500
      })
    });

    const resultWithContext = {
      ...payload,
      workbench_query: query
    };
    state.lastResult = resultWithContext;
    state.visualLoaded = false;
    clearVisualOutput('Generate a visual from the current result set.');
    renderResults(resultWithContext);
    showStatus('Query completed successfully.', 'success');
    saveDraftToStorage(false);
    // Auto-generate visualization in background so the Visuals tab is ready.
    _backgroundVisualize();
  } catch (error) {
    resetResultViews(error.message || 'Query failed.');
    showStatus(error.message || 'Query failed.', 'error');
  } finally {
    elements.runButton.disabled = false;
  }
}

async function generateQuery() {
  if (!elements.sourceSelect.value) {
    showStatus('Choose a connection or dataset before asking Helix to generate code.', 'error');
    return;
  }

  const prompt = elements.aiPrompt.value.trim();
  if (!prompt) {
    showStatus('Describe the query you want Helix to write first.', 'error');
    return;
  }

  showStatus('Helix is generating query code for the editor.', 'info');
  elements.generateButton.disabled = true;

  try {
    const payload = await fetchJson('/api/sql-workbench/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        secret: state.secret,
        data_source_id: elements.sourceSelect.value,
        prompt,
        entity_name: elements.entitySelect.value,
        query_language: currentMode()
      })
    });

    if (payload.query_language) {
      const generatedMode = syncModeOptions(payload.query_language);
      updateEditorMode(generatedMode);
    }

    setEditorText(payload.generated_query || '', false);
    showStatus('Generated query inserted into the editor.', 'success');
    saveDraftToStorage(false);
  } catch (error) {
    showStatus(error.message || 'Failed to generate query.', 'error');
  } finally {
    elements.generateButton.disabled = false;
  }
}

async function completeQueryWithAi() {
  if (!elements.sourceSelect.value) {
    showStatus('Choose a connection or dataset before using AI completion.', 'error');
    return;
  }
  const currentQuery = editorText().trim();
  if (!currentQuery) {
    applyAiRowCollapsed(false);
    showStatus('Start a query or describe the result you want in AI Draft.', 'info');
    elements.aiPrompt.focus();
    return;
  }

  const instruction = elements.aiPrompt.value.trim() ||
    'Complete this partial query using the available schema. Preserve its intent, keep it read-only, and return executable code only.';
  showStatus('Helix is completing the current query with schema context.', 'info');
  elements.aiCompleteButton.disabled = true;

  try {
    const payload = await fetchJson('/api/sql-workbench/generate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        secret: state.secret,
        data_source_id: elements.sourceSelect.value,
        prompt: instruction,
        current_query: currentQuery,
        entity_name: elements.entitySelect.value,
        query_language: currentMode()
      })
    });
    if (payload.query_language) {
      const generatedMode = syncModeOptions(payload.query_language);
      updateEditorMode(generatedMode);
    }
    const generatedQuery = String(payload.generated_query || '').trim();
    if (!generatedQuery) {
      throw new Error('AI completion returned an empty query.');
    }
    setEditorText(generatedQuery, false);
    state.lastTemplate = '';
    state.editor.focus();
    showStatus('AI completion applied. Undo restores the previous query.', 'success');
    saveDraftToStorage(false);
  } catch (error) {
    showStatus(error.message || 'Failed to complete the query.', 'error');
  } finally {
    elements.aiCompleteButton.disabled = false;
  }
}

async function fetchSuggestedQuery(entityName) {
  const sourceId = elements.sourceSelect ? elements.sourceSelect.value : '';
  const cacheKey = `helix.sqlwb.suggest.v1.${sourceId}.${entityName}`;

  // sessionStorage cache — avoid re-calling the AI endpoint within the same session.
  try {
    const cached = window.sessionStorage.getItem(cacheKey);
    if (cached) {
      const parsed = JSON.parse(cached);
      return parsed.suggested_query || null;
    }
  } catch (_e) { /* ignore storage errors */ }

  const entityInfo = state.entityInfoByName[entityName] || {};
  const schemaItems = schemaItemsForEntity(entityInfo);
  const schemaInfo = schemaItems.slice(0, 12)
    .map(col => `${col.name || col.field || ''} (${col.type || col.semantic_type || 'unknown'})`)
    .filter(s => s.trim())
    .join(', ');

  const result = await fetchJson('/api/sql-workbench/suggest-query', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      secret: state.secret,
      data_source_id: sourceId,
      entity_name: entityName,
      schema_info: schemaInfo
    })
  });

  if (result && result.suggested_query) {
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify(result));
    } catch (_e) { /* quota — swallow silently */ }
    return result.suggested_query;
  }

  return null;
}

async function addEntityToDashboard(entityName) {
  if (!entityName) {
    showStatus('No entity selected.', 'error');
    return;
  }

  // Only works when embedded in the main app where the dashboard API is available.
  const hasDashboard = typeof window.fetchAndRenderGraphForQuery === 'function'
    && window.dataAccessor
    && typeof window.dataAccessor.addGraphToCurrentDashboard === 'function';

  if (!hasDashboard) {
    showStatus('Dashboard integration is only available inside the main Helix app.', 'info');
    return;
  }

  showStatus(`Getting best query suggestion for ${entityName}...`, 'info');

  try {
    const nlQuery = await fetchSuggestedQuery(entityName);
    if (!nlQuery) {
      showStatus('Could not generate a query suggestion.', 'error');
      return;
    }

    showStatus(`Adding chart for "${entityName}" to dashboard...`, 'info');

    const graphId = Math.random().toString(36).substring(7);

    window.fetchAndRenderGraphForQuery(nlQuery, [], graphId)
      .then(() => {
        window.dataAccessor.addGraphToCurrentDashboard(window.userData, graphId, nlQuery);
        showStatus(`Chart for "${entityName}" added to dashboard.`, 'success');
      })
      .catch(err => {
        showStatus((err && err.message) || 'Failed to add chart to dashboard.', 'error');
      });

  } catch (err) {
    showStatus((err && err.message) || 'Failed to get query suggestion.', 'error');
  }
}

async function visualizeResults(forceRefresh = false, silent = false) {
  if (!state.lastResult || !Array.isArray(state.lastResult.data) || !state.lastResult.data.length) {
    clearVisualOutput();
    return;
  }
  if (state.visualLoaded && !forceRefresh) {
    return;
  }

  const prompt =
    elements.visualPrompt.value.trim() || 'Choose the most useful chart for these query results';
  elements.visualizeButton.disabled = true;
  elements.visualOutput.innerHTML =
    '<div class="sqlwb-empty">Generating a visualization from the current result set.</div>';

  try {
    const payload = await fetchJson('/api/sql-workbench/visualize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        secret: state.secret,
        prompt,
        rows: state.lastResult.data,
        data_source_id: elements.sourceSelect.value,
        source_type: selectedSource() ? selectedSource().source_type || '' : '',
        query_language: state.lastResult.query_language || currentMode(),
        entity_name: state.lastResult.entity_name || elements.entitySelect.value,
        columns: Array.isArray(state.lastResult.columns) ? state.lastResult.columns : [],
        original_query: editorText(),
        row_count: Number(state.lastResult.row_count) || 0
      })
    });

    renderVisualMeta(payload);
    if (!window.Plotly || !Array.isArray(payload.data)) {
      elements.visualOutput.innerHTML = `<div class="sqlwb-empty">${escapeHtml(
        JSON.stringify(payload, null, 2)
      )}</div>`;
      state.visualLoaded = true;
      return;
    }

    elements.visualOutput.innerHTML = '<div id="sqlwb-plotly-target" style="min-height: 320px;"></div>';
    await window.Plotly.newPlot(
      document.getElementById('sqlwb-plotly-target'),
      payload.data,
      payload.layout || {},
      { responsive: true, displaylogo: false }
    );
    state.visualLoaded = true;
  } catch (error) {
    renderVisualMeta(null);
    elements.visualOutput.innerHTML = `<div class="sqlwb-empty">${escapeHtml(error.message || 'Failed to generate a visual.')}</div>`;
    if (!silent) {
      showStatus(error.message || 'Failed to generate a visual.', 'error');
    }
  } finally {
    elements.visualizeButton.disabled = false;
  }
}

/**
 * Kick off graph generation in the background after a query completes.
 * Silently swallows errors — background visualization is best-effort.
 */
function _backgroundVisualize() {
  if (!state.lastResult) return;
  const rows = state.lastResult.data || state.lastResult.rows;
  if (!Array.isArray(rows) || rows.length === 0) return;

  const pageRoot = root || (typeof document !== 'undefined' ? document.getElementById('sql-workbench-page') : null);

  visualizeResults(false, true).then(() => {
    if (state.visualLoaded && pageRoot) {
      const visualTab = pageRoot.querySelector('[data-workbench-tab="visuals"]');
      if (visualTab) {
        visualTab.dataset.visualReady = '1';
      }
    }
  }).catch(() => {
    // Swallow: background visualization errors should not disrupt the user.
  });
}

function switchTab(tabName) {
  const pageRoot = root || (typeof document !== 'undefined' ? document.getElementById('sql-workbench-page') : null);
  if (!pageRoot) {
    return;
  }
  state.activeTab = tabName;
  pageRoot.querySelectorAll('.sqlwb-tab').forEach(button => {
    button.classList.toggle('is-active', button.dataset.workbenchTab === tabName);
  });
  pageRoot.querySelectorAll('.sqlwb-panel').forEach(panel => {
    panel.classList.toggle('is-active', panel.id === `sqlwb-${tabName}-panel`);
  });
  if (tabName === 'visuals') {
    visualizeResults(false);
  }
}

function debounce(callback, delayMs) {
  let timeoutId = 0;
  return (...args) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => callback(...args), delayMs);
  };
}

function openChatPanel() {
  if (!elements.chatPanel || !elements.app) return;
  state.chatOpen = true;
  elements.chatPanel.hidden = false;
  elements.app.classList.add('is-chat-open');
  renderChatMessages();
  if (elements.chatInput) elements.chatInput.focus();
}

function closeChatPanel() {
  if (!elements.chatPanel || !elements.app) return;
  state.chatOpen = false;
  elements.chatPanel.hidden = true;
  elements.app.classList.remove('is-chat-open');
}

function renderChatMessages() {
  if (!elements.chatMessages) return;
  const html = state.chatHistory.length ? state.chatHistory.map(msg => {
    const cls = msg.role === 'user' ? 'sqlwb-chat-msg--user' : 'sqlwb-chat-msg--assistant';
    return `<div class="sqlwb-chat-msg ${cls}">${escapeHtml(msg.content)}</div>`;
  }).join('') : `<div class="sqlwb-chat-empty">
    <div><strong>Work with the query in context.</strong><br>Ask for a draft, an explanation, or a safer version. Suggestions stay reviewable before they reach the editor.</div>
    <div class="sqlwb-chat-prompts" aria-label="Suggested questions">
      <button class="sqlwb-chat-prompt" type="button" data-prompt="Draft a useful query for the selected table">Draft from this table</button>
      <button class="sqlwb-chat-prompt" type="button" data-prompt="Explain the current query and call out any risks">Explain this query</button>
      <button class="sqlwb-chat-prompt" type="button" data-prompt="Make the current query safer and more efficient">Improve safety and performance</button>
    </div>
  </div>`;
  elements.chatMessages.innerHTML = html;
  elements.chatMessages.scrollTop = elements.chatMessages.scrollHeight;
}

function renderDiffSuggestion(suggestedQuery) {
  if (!elements.chatMessages || !suggestedQuery) return;
  const diffHtml = `<div class="sqlwb-chat-diff">
    <div style="font-weight:600;margin-bottom:6px;">Suggested query:</div>
    <pre>${escapeHtml(suggestedQuery.slice(0, 800))}</pre>
    <div class="sqlwb-chat-diff-actions">
      <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored mdl-js-ripple-effect sqlwb-chat-apply-btn" type="button" data-query="${escapeHtml(suggestedQuery).replace(/"/g, '&quot;')}">Apply to editor</button>
      <button class="mdl-button mdl-js-button sqlwb-chat-dismiss-btn" type="button">Dismiss</button>
    </div>
  </div>`;
  elements.chatMessages.insertAdjacentHTML('beforeend', diffHtml);
  elements.chatMessages.scrollTop = elements.chatMessages.scrollHeight;
}

async function sendChatMessage() {
  if (!elements.chatInput || state.chatLoading) return;
  const message = elements.chatInput.value.trim();
  if (!message) return;

  elements.chatInput.value = '';
  state.chatHistory.push({ role: 'user', content: message });
  renderChatMessages();

  state.chatLoading = true;
  if (elements.chatSendBtn) elements.chatSendBtn.disabled = true;
  elements.chatMessages.insertAdjacentHTML('beforeend',
    '<div class="sqlwb-chat-msg sqlwb-chat-msg--loading" id="sqlwb-chat-loading">Thinking...</div>'
  );
  elements.chatMessages.scrollTop = elements.chatMessages.scrollHeight;
  const loadingEl = document.getElementById('sqlwb-chat-loading');

  try {
    const payload = {
      secret: state.secret,
      data_source_id: elements.sourceSelect ? elements.sourceSelect.value : '',
      message,
      current_query: state.editor ? state.editor.state.doc.toString().slice(0, 1000) : '',
      entity_name: elements.entitySelect ? elements.entitySelect.value : '',
      query_language: currentMode(),
      conversation: state.chatHistory.slice(-8).map(m => ({ role: m.role, content: m.content.slice(0, 400) }))
    };

    const response = await fetch('/api/sql-workbench/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();

    if (data.success) {
      state.chatHistory.push({ role: 'assistant', content: data.message || '' });
      renderChatMessages();
      if (data.suggested_query) {
        renderDiffSuggestion(data.suggested_query);
      }
    } else {
      state.chatHistory.push({ role: 'assistant', content: 'Error: ' + (data.error || 'Unknown error') });
      renderChatMessages();
    }
  } catch (err) {
    state.chatHistory.push({ role: 'assistant', content: 'Connection error. Please try again.' });
    renderChatMessages();
  } finally {
    if (loadingEl) loadingEl.remove();
    state.chatLoading = false;
    if (elements.chatSendBtn) elements.chatSendBtn.disabled = false;
    if (elements.chatInput) elements.chatInput.focus();
  }
}

function initContextMenu() {
  const menu = document.createElement('div');
  menu.id = 'sqlwb-ctx-menu';
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'Entity actions');
  menu.hidden = true;
  menu.innerHTML = `
    <button data-ctx-action="view-erd" role="menuitem">View ERD</button>
    <button data-ctx-action="add-to-dashboard" role="menuitem">Add to Dashboard</button>
    <hr role="separator">
    <button data-ctx-action="copy-name" role="menuitem">Copy table name</button>
  `;
  document.body.appendChild(menu);
  elements.ctxMenu = menu;

  const style = document.createElement('style');
  style.textContent = `
    #sqlwb-ctx-menu {
      position: fixed;
      z-index: 9999;
      min-width: 180px;
      padding: 6px 0;
      background: #fff;
      border: 1px solid rgba(23,34,57,0.12);
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(27,40,66,0.14);
    }
    #sqlwb-ctx-menu button {
      display: block;
      width: 100%;
      padding: 9px 16px;
      border: none;
      background: transparent;
      text-align: left;
      font: inherit;
      font-size: 0.88rem;
      color: #10203b;
      cursor: pointer;
    }
    #sqlwb-ctx-menu button:hover { background: rgba(15,111,255,0.07); }
    #sqlwb-ctx-menu hr { margin: 4px 0; border: none; border-top: 1px solid rgba(23,34,57,0.08); }
  `;
  document.head.appendChild(style);
}

function insertIntoEditorFromBridge(text, { replace = false, run = false } = {}) {
  if (!state.editor) return;
  const insert = String(text || '');
  if (!insert) return;
  const doc = state.editor.state.doc;
  if (replace) {
    state.editor.dispatch({ changes: { from: 0, to: doc.length, insert } });
  } else {
    const current = doc.toString();
    const needsSpace = current && !/\s$/.test(current);
    state.editor.dispatch({
      changes: { from: doc.length, to: doc.length, insert: (needsSpace ? ' ' : '') + insert }
    });
  }
  try { state.editor.focus(); } catch (_) {}
  if (run) {
    setTimeout(() => {
      try { runQuery(); } catch (err) { console.error('Context menu run failed', err); }
    }, 30);
  }
}

function bindTreeContextMenuBridge() {
  document.addEventListener('tree-context-menu:insert', (event) => {
    if (!state.editor) return;
    event.preventDefault();
    const detail = event.detail || {};
    insertIntoEditorFromBridge(detail.text, { replace: !!detail.replace, run: !!detail.run });
  });

  document.addEventListener('tree-context-menu:prompt', (event) => {
    if (!elements.aiPrompt) return;
    event.preventDefault();
    const detail = event.detail || {};
    const prompt = String(detail.prompt || '');
    if (!prompt) return;
    elements.aiPrompt.value = prompt;
    elements.aiPrompt.dispatchEvent(new Event('input', { bubbles: true }));
    if (elements.aiRow && elements.aiRow.hidden) {
      try { applyAiRowCollapsed(false); } catch (_) {}
    }
    try { elements.aiPrompt.focus(); } catch (_) {}
  });
}

function openContextMenu(clientX, clientY) {
  const menu = elements.ctxMenu;
  if (!menu) return;
  menu.hidden = false;
  menu.style.left = '-9999px';
  menu.style.top = '-9999px';
  const rect = menu.getBoundingClientRect();
  const left = Math.min(clientX, window.innerWidth - rect.width - 8);
  const top = Math.min(clientY, window.innerHeight - rect.height - 8);
  menu.style.left = `${Math.max(8, left)}px`;
  menu.style.top = `${Math.max(8, top)}px`;
}

function closeContextMenu() {
  if (elements.ctxMenu) {
    elements.ctxMenu.hidden = true;
    state.ctxTargetEntityName = '';
  }
}

function copyEntityNameToClipboard(entityName) {
  if (!entityName) return;
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(entityName)
      .then(() => showStatus(`Copied "${entityName}" to clipboard.`, 'success'))
      .catch(() => showStatus('Copy failed.', 'error'));
  } else {
    const ta = document.createElement('textarea');
    ta.value = entityName;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      showStatus(`Copied "${entityName}" to clipboard.`, 'success');
    } catch (_e) {
      showStatus('Copy not supported in this browser.', 'error');
    }
    document.body.removeChild(ta);
  }
}

function bindContextMenuEvents() {
  elements.entitySelect.addEventListener('contextmenu', (event) => {
    const selectedValue = elements.entitySelect.value;
    if (!selectedValue) { event.preventDefault(); return; }
    event.preventDefault();
    state.ctxTargetEntityName = selectedValue;
    openContextMenu(event.clientX, event.clientY);
  });

  elements.schemaOutput.addEventListener('contextmenu', (event) => {
    const entityName = elements.entitySelect.value;
    if (!entityName) { event.preventDefault(); return; }
    event.preventDefault();
    state.ctxTargetEntityName = entityName;
    openContextMenu(event.clientX, event.clientY);
  });

  document.addEventListener('click', (event) => {
    if (elements.ctxMenu && !elements.ctxMenu.hidden) {
      if (!elements.ctxMenu.contains(event.target)) closeContextMenu();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && elements.ctxMenu && !elements.ctxMenu.hidden) closeContextMenu();
  });

  window.addEventListener('scroll', () => { if (elements.ctxMenu) closeContextMenu(); }, { passive: true });

  elements.ctxMenu.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ctx-action]');
    if (!button) return;
    const action = button.dataset.ctxAction;
    const entityName = state.ctxTargetEntityName;
    closeContextMenu();
    if (action === 'view-erd') {
      openErdModal(entityName);
    } else if (action === 'add-to-dashboard') {
      addEntityToDashboard(entityName);
    } else if (action === 'copy-name') {
      copyEntityNameToClipboard(entityName);
    }
  });
}

async function fetchErdData(entityName) {
  const sourceId = elements.sourceSelect ? elements.sourceSelect.value : '';
  const cacheKey = `helix.sqlwb.erd.v1.${sourceId}.${entityName || '__all__'}`;

  try {
    const cached = window.sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch (_e) { /* ignore */ }

  const params = new URLSearchParams({ secret: state.secret, data_source_id: sourceId });
  if (entityName) params.set('entity_name', entityName);

  const data = await fetchJson(apiUrl(`/api/sql-workbench/erd?${params.toString()}`));

  try {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(data));
  } catch (_e) { /* quota */ }

  return data;
}

function renderErd(svgEl, erdData) {
  const d3 = window.d3;
  if (!d3) {
    svgEl.innerHTML = '<text x="50%" y="50%" text-anchor="middle" font-size="14" fill="#6b7889">D3.js is required for ERD rendering.</text>';
    return;
  }

  const nodes = (erdData.nodes || []).map(n => ({ ...n }));
  const edges = (erdData.edges || []).map(e => ({ ...e }));

  if (nodes.length === 0) {
    svgEl.innerHTML = '<text x="50%" y="50%" text-anchor="middle" font-size="14" fill="#6b7889">No tables found.</text>';
    return;
  }

  const NODE_WIDTH = 180;
  const NODE_HEADER_H = 32;
  const NODE_ROW_H = 20;
  const MAX_COLS = 8;

  function nodeHeight(n) {
    const cols = Math.min((n.columns || []).length, MAX_COLS);
    const extraRow = (n.columns || []).length > MAX_COLS ? 1 : 0;
    return NODE_HEADER_H + (cols + extraRow) * NODE_ROW_H + 8;
  }

  const w = svgEl.clientWidth || 800;
  const h = svgEl.clientHeight || 600;

  const svg = d3.select(svgEl);
  svg.selectAll('*').remove();

  const container = svg.append('g').attr('class', 'erd-container');

  svg.append('defs').append('marker')
    .attr('id', 'erd-arrow')
    .attr('viewBox', '0 -5 10 10')
    .attr('refX', 10).attr('refY', 0)
    .attr('markerWidth', 6).attr('markerHeight', 6)
    .attr('orient', 'auto')
    .append('path').attr('d', 'M0,-5L10,0L0,5').attr('fill', '#7094c4');

  const simulation = d3.forceSimulation(nodes)
    .force('link', d3.forceLink(edges).id(d => d.id).distance(240).strength(0.4))
    .force('charge', d3.forceManyBody().strength(-600))
    .force('center', d3.forceCenter(w / 2, h / 2))
    .force('collision', d3.forceCollide().radius(d => Math.max(NODE_WIDTH, nodeHeight(d)) * 0.7 + 20));

  const link = container.append('g').selectAll('line').data(edges).join('line')
    .attr('stroke', d => d.inferred ? '#b0b8cc' : '#7094c4')
    .attr('stroke-width', 1.5)
    .attr('stroke-dasharray', d => d.inferred ? '5,3' : null)
    .attr('marker-end', 'url(#erd-arrow)');

  const linkLabel = container.append('g').selectAll('text').data(edges).join('text')
    .attr('font-size', '10px').attr('fill', '#8495ae').attr('text-anchor', 'middle')
    .text(d => d.label || '');

  const node = container.append('g').selectAll('g').data(nodes).join('g')
    .attr('class', 'erd-node')
    .call(d3.drag()
      .on('start', (e, d) => { if (!e.active) simulation.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
      .on('drag', (e, d) => { d.fx = e.x; d.fy = e.y; })
      .on('end', (e, d) => { if (!e.active) simulation.alphaTarget(0); d.fx = null; d.fy = null; }));

  node.append('rect').attr('width', NODE_WIDTH).attr('height', d => nodeHeight(d))
    .attr('rx', 8).attr('fill', '#f8fbff').attr('stroke', '#c5d3e8').attr('stroke-width', 1.5);
  node.append('rect').attr('width', NODE_WIDTH).attr('height', NODE_HEADER_H)
    .attr('rx', 8).attr('fill', '#10203b');
  node.append('rect').attr('y', NODE_HEADER_H - 8).attr('width', NODE_WIDTH).attr('height', 8).attr('fill', '#10203b');
  node.append('text').attr('x', 10).attr('y', 20).attr('font-size', '12px').attr('font-weight', '700')
    .attr('fill', '#fff').text(d => d.label || d.id);
  node.append('text').attr('x', NODE_WIDTH - 8).attr('y', 20).attr('text-anchor', 'end')
    .attr('font-size', '10px').attr('fill', 'rgba(255,255,255,0.55)')
    .text(d => d.row_count != null ? String(d.row_count).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '');

  node.each(function(d) {
    const g = d3.select(this);
    const cols = (d.columns || []).slice(0, MAX_COLS);
    cols.forEach((col, i) => {
      const y = NODE_HEADER_H + i * NODE_ROW_H + 14;
      if (col.pk) g.append('text').attr('x', 8).attr('y', y).attr('font-size', '9px').attr('fill', '#e8a620').text('PK');
      g.append('text').attr('x', col.pk ? 26 : 8).attr('y', y).attr('font-size', '11px').attr('fill', '#253047').text(col.name);
      g.append('text').attr('x', NODE_WIDTH - 6).attr('y', y).attr('text-anchor', 'end').attr('font-size', '10px').attr('fill', '#8495ae').text(col.type);
    });
    if ((d.columns || []).length > MAX_COLS) {
      const y = NODE_HEADER_H + MAX_COLS * NODE_ROW_H + 14;
      g.append('text').attr('x', 8).attr('y', y).attr('font-size', '10px').attr('fill', '#8495ae')
        .text(`+${d.columns.length - MAX_COLS} more`);
    }
  });

  simulation.on('tick', () => {
    link
      .attr('x1', d => (d.source.x || 0) + NODE_WIDTH / 2)
      .attr('y1', d => (d.source.y || 0) + nodeHeight(d.source) / 2)
      .attr('x2', d => (d.target.x || 0) + NODE_WIDTH / 2)
      .attr('y2', d => (d.target.y || 0) + nodeHeight(d.target) / 2);
    linkLabel
      .attr('x', d => ((d.source.x || 0) + (d.target.x || 0)) / 2 + NODE_WIDTH / 2)
      .attr('y', d => ((d.source.y || 0) + (d.target.y || 0)) / 2 + nodeHeight(d.source) / 2);
    node.attr('transform', d => `translate(${(d.x || 0) - NODE_WIDTH / 2},${(d.y || 0) - nodeHeight(d) / 2})`);
  });

  const zoom = d3.zoom().scaleExtent([0.15, 4]).on('zoom', (e) => container.attr('transform', e.transform));
  svg.call(zoom);

  if (elements.erdResetZoom) {
    elements.erdResetZoom.onclick = () => svg.transition().duration(400).call(zoom.transform, d3.zoomIdentity);
  }

  return simulation;
}

async function openErdModal(entityName) {
  const dialog = elements.erdDialog || document.getElementById('sqlwb-erd-dialog');
  if (!dialog) return;

  const titleEl = elements.erdTitle || document.getElementById('sqlwb-erd-title');
  const loadingEl = elements.erdLoading || document.getElementById('sqlwb-erd-loading');
  const errorEl = elements.erdError || document.getElementById('sqlwb-erd-error');
  const noticeEl = elements.erdNotice || document.getElementById('sqlwb-erd-notice');
  const hintEl = elements.erdHint || document.getElementById('sqlwb-erd-hint');
  const svgEl = elements.erdSvg || document.getElementById('sqlwb-erd-svg');
  const legendEl = elements.erdLegend || document.getElementById('sqlwb-erd-legend');

  if (titleEl) titleEl.textContent = entityName ? `ERD \u2014 ${entityName}` : 'Entity Relationship Diagram';
  if (loadingEl) loadingEl.hidden = false;
  if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
  if (noticeEl) { noticeEl.hidden = true; noticeEl.textContent = ''; }
  if (hintEl) hintEl.hidden = true;
  if (svgEl) svgEl.innerHTML = '';
  if (legendEl) legendEl.textContent = '';

  dialog.showModal();

  try {
    const erdData = await fetchErdData(entityName);

    if (loadingEl) loadingEl.hidden = true;

    if (erdData.note && (!erdData.nodes || erdData.nodes.length === 0)) {
      if (errorEl) { errorEl.hidden = false; errorEl.textContent = erdData.note; }
      return;
    }

    if (erdData.truncated && noticeEl) {
      noticeEl.hidden = false;
      noticeEl.textContent = `Showing 60 of ${erdData.total_table_count} tables. Right-click a specific entity for a focused view.`;
    }

    if (hintEl) hintEl.hidden = false;
    const simulation = renderErd(svgEl, erdData);
    if (simulation && dialog) {
      dialog.addEventListener('close', () => simulation.stop(), { once: true });
    }

    if (legendEl) {
      const parts = [];
      if (erdData.inferred_edge_count > 0) parts.push(`${erdData.inferred_edge_count} inferred relationship${erdData.inferred_edge_count !== 1 ? 's' : ''} (dashed)`);
      const definedCount = (erdData.edges || []).length - erdData.inferred_edge_count;
      if (definedCount > 0) parts.push(`${definedCount} defined FK${definedCount !== 1 ? 's' : ''}`);
      if (!parts.length) parts.push('No relationships found');
      legendEl.textContent = parts.join(' \u00b7 ');
    }
  } catch (err) {
    if (loadingEl) loadingEl.hidden = true;
    if (errorEl) { errorEl.hidden = false; errorEl.textContent = err.message || 'Failed to load ERD.'; }
  }
}

function wireSavedDraftEvents() {
  if (!elements.savedList) {
    return;
  }

  elements.savedList.addEventListener('click', event => {
    // Snippet insert / delete (server-backed)
    const insertBtn = event.target.closest('.sqlwb-snippet-insert-btn');
    if (insertBtn) {
      insertSnippetIntoEditor(insertBtn.dataset.query, insertBtn.dataset.lang);
      return;
    }
    const deleteBtn = event.target.closest('.sqlwb-snippet-delete-btn');
    if (deleteBtn) {
      deleteSnippet(deleteBtn.dataset.id);
      return;
    }

    // Legacy localStorage draft load / delete
    const target = event.target.closest('[data-saved-action]');
    if (!target) {
      return;
    }

    const draftId = target.getAttribute('data-draft-id') || '';
    const action = target.getAttribute('data-saved-action') || '';
    const savedDraft = getSavedDrafts().find(item => item.id === draftId);
    if (!savedDraft) {
      return;
    }

    if (action === 'load') {
      restoreDraft(savedDraft);
      return;
    }

    if (action === 'delete') {
      deleteSavedDraft(draftId);
      showStatus('Saved draft removed.', 'success');
    }
  });
}

function bindEvents() {
  elements.sourceSelect.addEventListener('change', () => {
    onSourceChange().catch(error => {
      showStatus(error.message || 'Failed to switch connection.', 'error');
    });
  });

  elements.openConnectionButton.addEventListener('click', () => {
    if (window.DataConnectors && typeof window.DataConnectors.open === 'function') {
      window.DataConnectors.open();
    } else {
      // Fallback to inline form when data-connectors.js is unavailable
      if (!state.connectionFormOpen && !elements.connectionNameInput.value.trim()) {
        resetConnectionForm();
      }
      toggleConnectionPanel();
    }
  });

  elements.cancelConnectionButton.addEventListener('click', () => {
    resetConnectionForm();
    toggleConnectionPanel(false);
    if (!state.dataSources.length) {
      showStatus('Create a connection or upload a dataset to start using the workbench.', 'info');
      return;
    }
    clearStatus();
  });

  elements.connectionTypeSelect.addEventListener('change', () => {
    elements.connectionPortInput.value = '';
    updateConnectionForm();
  });

  elements.connectionInputModeSelect.addEventListener('change', () => {
    updateConnectionForm();
  });

  elements.saveConnectionButton.addEventListener('click', () => {
    createConnection();
  });

  elements.entitySelect.addEventListener('change', () => {
    const entityName = elements.entitySelect.value;
    const nextTemplate = buildTemplateForMode(currentMode(), entityName, selectedEntityInfo(), selectedSource());
    maybeApplyTemplate(nextTemplate);
    resetResultViews();
    rememberEntityForSource(elements.sourceSelect.value, entityName);
    renderEntityTree();
    loadEntitySchema(entityName).catch(error => {
      showStatus(error.message || 'Failed to load entity schema.', 'error');
    });
  });

  if (elements.entityTree) {
    elements.entityTree.addEventListener('click', event => {
      const leaf = event.target.closest('.sqlwb-tree-leaf');
      if (!leaf || !elements.entityTree.contains(leaf)) return;
      const value = leaf.dataset.entityValue || '';
      if (value) selectEntityFromTree(value);
    });
    elements.entityTree.addEventListener('toggle', event => {
      const details = event.target;
      if (!details || !details.classList || !details.classList.contains('sqlwb-tree-group')) return;
      const key = details.dataset.treeKey;
      if (!key) return;
      const filtering = !!(elements.entityFilter && elements.entityFilter.value.trim());
      if (filtering) return;
      setTreeGroupExpanded(key, !!details.open);
    }, true);
  }

  if (elements.entityFilter) {
    elements.entityFilter.addEventListener('input', () => {
      renderEntityTree();
    });
  }

  if (elements.treeExpandAll) {
    elements.treeExpandAll.addEventListener('click', () => {
      setAllTreeGroupsExpanded(true);
    });
  }

  if (elements.treeCollapseAll) {
    elements.treeCollapseAll.addEventListener('click', () => {
      setAllTreeGroupsExpanded(false);
    });
  }

  if (elements.aiToggle && elements.aiRow) {
    elements.aiToggle.addEventListener('click', () => {
      const nowCollapsed = !elements.aiRow.hidden;
      applyAiRowCollapsed(nowCollapsed);
      setStoredJson(STORAGE_KEYS.aiPromptCollapsed, nowCollapsed);
      if (!nowCollapsed && elements.aiPrompt) {
        elements.aiPrompt.focus();
      }
    });
  }

  elements.modeSelect.addEventListener('change', () => {
    const mode = currentMode();
    updateEditorMode(mode);
    maybeApplyTemplate(buildTemplateForMode(mode, elements.entitySelect.value, selectedEntityInfo(), selectedSource()));
    resetResultViews();
    validateEditorContent();
  });

  elements.runButton.addEventListener('click', () => {
    runQuery();
  });

  elements.aiCompleteButton.addEventListener('click', () => {
    completeQueryWithAi();
  });

  elements.generateButton.addEventListener('click', () => {
    generateQuery();
  });

  elements.visualizeButton.addEventListener('click', () => {
    setActiveTab('visuals');
    visualizeResults(true);
  });

  elements.saveButton.addEventListener('click', () => {
    if (!elements.titleInput.value.trim()) {
      elements.titleInput.value = titleFromState();
    }
    saveSnippetToServer();
  });

  if (elements.shareButton) {
    elements.shareButton.addEventListener('click', () => {
      shareWorkbench();
    });
  }

  const copyShareBtn = document.getElementById('sqlwb-share-copy-btn');
  if (copyShareBtn) {
    copyShareBtn.addEventListener('click', () => {
      const urlInput = document.getElementById('sqlwb-share-url-input');
      if (urlInput) {
        urlInput.select();
        navigator.clipboard && navigator.clipboard.writeText(urlInput.value).catch(() => {
          document.execCommand('copy');
        });
        copyShareBtn.textContent = 'Copied!';
        setTimeout(() => { copyShareBtn.textContent = 'Copy'; }, 2000);
      }
    });
  }

  const closeShareBtn = document.getElementById('sqlwb-share-close-btn');
  if (closeShareBtn) {
    closeShareBtn.addEventListener('click', closeShareDialog);
  }

  elements.newButton.addEventListener('click', () => {
    state.currentDraftId = '';
    state.currentShareId = '';
    elements.titleInput.value = '';
    elements.aiPrompt.value = '';
    elements.visualPrompt.value = '';
    setEditorText(
      buildTemplateForMode(currentMode(), elements.entitySelect.value, selectedEntityInfo(), selectedSource()),
      true
    );
    resetResultViews();
    renderSchema({});
    setStoredJson(STORAGE_KEYS.draft, null);
    validateEditorContent();
    showStatus('Started a new draft.', 'success');
  });

  const pageRoot = root || (typeof document !== 'undefined' ? document.getElementById('sql-workbench-page') : null);
  if (pageRoot) {
    pageRoot.querySelectorAll('.sqlwb-tab').forEach(button => {
      button.addEventListener('click', () => {
        switchTab(button.dataset.workbenchTab || 'results');
      });
    });
  }

  if (elements.profileOutput) {
    elements.profileOutput.addEventListener('click', event => {
      const button = event.target.closest('[data-profile-action]');
      if (button && elements.profileOutput.contains(button)) {
        handleProfileAction(button);
      }
    });
  }

  if (elements.resultsMeta) {
    elements.resultsMeta.addEventListener('click', event => {
      const button = event.target.closest('[data-profile-column-target]');
      if (!button || !elements.resultsMeta.contains(button)) {
        return;
      }
      const columnName = button.dataset.profileColumnTarget || '';
      const profileIndex = state.resultProfiles.findIndex(profile => profile.name === columnName);
      if (profileIndex < 0) {
        return;
      }
      switchTab('profile');
      const card = document.getElementById(`sqlwb-profile-${profileIndex}`);
      if (card) {
        const panel = document.getElementById('sqlwb-profile-panel');
        const cardRect = card.getBoundingClientRect();
        const panelRect = panel ? panel.getBoundingClientRect() : null;
        if (
          panelRect &&
          (cardRect.top < panelRect.top || cardRect.top >= panelRect.bottom - 40)
        ) {
          card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        card.focus({ preventScroll: true });
      }
    });
  }

  const persistDraft = debounce(() => {
    if (!editorText().trim()) {
      return;
    }
    if (!elements.titleInput.value.trim()) {
      elements.titleInput.value = titleFromState();
    }
    saveDraftToStorage(false);
  }, 500);

  elements.titleInput.addEventListener('input', persistDraft);
  elements.aiPrompt.addEventListener('input', persistDraft);
  elements.visualPrompt.addEventListener('input', persistDraft);

  wireSavedDraftEvents();

  if (elements.snippetSearch) {
    elements.snippetSearch.addEventListener(
      'input',
      debounce(e => {
        loadSnippets(e.target.value.trim()).catch(() => {});
      }, 300)
    );
  }

  if (elements.chatToggleBtn) {
    elements.chatToggleBtn.addEventListener('click', () => {
      if (state.chatOpen) { closeChatPanel(); } else { openChatPanel(); }
    });
  }
  if (elements.chatCloseBtn) {
    elements.chatCloseBtn.addEventListener('click', closeChatPanel);
  }
  if (elements.chatSendBtn) {
    elements.chatSendBtn.addEventListener('click', sendChatMessage);
  }
  if (elements.chatInput) {
    elements.chatInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); }
    });
  }
  if (elements.chatMessages) {
    elements.chatMessages.addEventListener('click', (e) => {
      const promptBtn = e.target.closest('.sqlwb-chat-prompt');
      if (promptBtn && elements.chatInput) {
        elements.chatInput.value = promptBtn.dataset.prompt || '';
        elements.chatInput.focus();
        return;
      }
      const applyBtn = e.target.closest('.sqlwb-chat-apply-btn');
      if (applyBtn) {
        const query = applyBtn.dataset.query;
        if (query) {
          setEditorText(query);
          closeChatPanel();
          showStatus('Query applied from AI suggestion.', 'success');
        }
        return;
      }
      const dismissBtn = e.target.closest('.sqlwb-chat-dismiss-btn');
      if (dismissBtn) {
        const diffCard = dismissBtn.closest('.sqlwb-chat-diff');
        if (diffCard) diffCard.remove();
      }
    });
  }

  const erdCloseBtn = document.getElementById('sqlwb-erd-close');
  if (erdCloseBtn) {
    erdCloseBtn.addEventListener('click', () => {
      if (elements.erdDialog) elements.erdDialog.close();
    });
  }

  if (elements.erdDialog) {
    elements.erdDialog.addEventListener('click', (e) => {
      if (e.target === elements.erdDialog) elements.erdDialog.close();
    });
  }

  bindContextMenuEvents();
}

/**
 * SQL linter: flags write operations (blocked by the backend read-only check).
 * @returns {import('@codemirror/lint').Diagnostic[]}
 */
function sqlLintSource(view) {
  const text = view.state.doc.toString().trimStart();
  if (!text) return [];
  if (SQL_WRITE_KEYWORDS_RE.test(text)) {
    const end = Math.min(text.search(/\s|;/), 30);
    return [{
      from: 0,
      to: end > 0 ? end : Math.min(text.length, 20),
      severity: 'error',
      message: 'Write operations are blocked. Only SELECT, WITH, SHOW, DESCRIBE, EXPLAIN, PRAGMA, and VALUES are allowed.'
    }];
  }
  return [];
}

/**
 * JSON linter for Elasticsearch Query DSL.
 * @returns {import('@codemirror/lint').Diagnostic[]}
 */
function jsonLintSource(view) {
  const text = view.state.doc.toString();
  if (!text.trim()) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return [{
        from: 0,
        to: 1,
        severity: 'warning',
        message: 'Expected a JSON object (not an array) for Elasticsearch Query DSL.'
      }];
    }
    return [];
  } catch (e) {
    const posMatch = e.message && e.message.match(/position\s+(\d+)/i);
    const pos = posMatch ? parseInt(posMatch[1], 10) : 0;
    const safePos = Math.min(pos, Math.max(0, view.state.doc.length - 1));
    return [{
      from: safePos,
      to: Math.min(safePos + 1, view.state.doc.length),
      severity: 'error',
      message: 'JSON parse error: ' + (e.message || 'Invalid JSON')
    }];
  }
}

/**
 * MongoDB linter: warns if content looks like it's missing a db. reference.
 * @returns {import('@codemirror/lint').Diagnostic[]}
 */
function mongoLintSource(view) {
  const text = view.state.doc.toString().trim();
  if (text.length < 10) return [];
  if (!/\bdb\s*\./.test(text)) {
    return [{
      from: 0,
      to: Math.min(text.length, 30),
      severity: 'warning',
      message: 'Expected a db.collection.method() call (e.g. db.users.find({}))'
    }];
  }
  return [];
}

/**
 * Returns the appropriate lint source function for the given editor mode.
 * @param {string} mode
 * @returns {import('@codemirror/lint').LintSource}
 */
function lintSourceForMode(mode) {
  if (mode === 'elasticsearch') return jsonLintSource;
  if (mode === 'mongodb') return mongoLintSource;
  return sqlLintSource;
}

async function shareWorkbench() {
  const query = editorText();
  if (!query.trim()) {
    showStatus('Write a query first before sharing.', 'error');
    return;
  }
  try {
    showStatus('Creating share link...', 'info');
    const data = await fetchJson('/api/sql-workbench/share', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        secret: state.secret,
        query,
        data_source_id: (elements.sourceSelect && elements.sourceSelect.value) || '',
        query_language: currentMode(),
        entity_name: (elements.entitySelect && elements.entitySelect.value) || '',
        title: (elements.titleInput && elements.titleInput.value.trim()) || titleFromState(),
        base_share_id: state.currentShareId || '',
      }),
    });
    state.currentShareId = data.share_id;
    const shareUrl = window.location.origin + data.share_url;
    showShareDialog(shareUrl, data.is_fork);
    clearStatus();
  } catch (error) {
    showStatus(error.message || 'Failed to create share link.', 'error');
  }
}

async function loadSharedQuery(shareId) {
  try {
    showStatus('Loading shared query...', 'info');
    const data = await fetchJson(`/api/sql-workbench/share/${shareId}`);
    state.currentShareId = shareId;
    if (data.title && elements.titleInput) {
      elements.titleInput.value = data.title;
    }
    const modeMap = { sql: 'sql', mongodb: 'mongodb', elasticsearch: 'elasticsearch', python: 'python' };
    const mode = modeMap[(data.query_language || '').toLowerCase()] || 'sql';
    if (elements.modeSelect) {
      elements.modeSelect.value = mode;
      updateEditorMode(mode);
    }
    if (data.data_source_id) {
      const sourceExists = state.dataSources.some(s => s.id === data.data_source_id);
      if (sourceExists && elements.sourceSelect) {
        elements.sourceSelect.value = data.data_source_id;
        await onSourceChange({ preferredEntityName: data.entity_name || '' });
      }
    }
    if (data.entity_name && elements.entitySelect) {
      elements.entitySelect.value = data.entity_name;
    }
    setEditorText(data.query || '', false);
    showStatus(
      data.forked_from ? 'Shared query loaded (fork).' : 'Shared query loaded.',
      'success'
    );
  } catch (error) {
    showStatus(error.message || 'Failed to load shared query.', 'error');
  }
}

function showShareDialog(shareUrl, isFork) {
  const dialog = document.getElementById('sqlwb-share-dialog');
  const urlInput = document.getElementById('sqlwb-share-url-input');
  const forkNote = document.getElementById('sqlwb-share-fork-note');
  if (!dialog || !urlInput) {
    // Fallback: copy to clipboard directly
    navigator.clipboard && navigator.clipboard.writeText(shareUrl).catch(() => {});
    showStatus('Share link copied to clipboard.', 'success');
    return;
  }
  urlInput.value = shareUrl;
  if (forkNote) {
    forkNote.hidden = !isFork;
  }
  dialog.hidden = false;
}

function closeShareDialog() {
  const dialog = document.getElementById('sqlwb-share-dialog');
  if (dialog) dialog.hidden = true;
}

function initializeEditor() {
  const editorTheme = EditorView.theme({
    '&': {
      height: '360px',
      fontSize: '14px'
    },
    '.cm-scroller': {
      fontFamily:
        '"Iosevka Web", "SFMono-Regular", "SF Mono", "IBM Plex Mono", Consolas, monospace',
      lineHeight: '1.65'
    },
    '.cm-content': {
      padding: '16px 18px',
      textAlign: 'left'
    },
    '.cm-line': {
      textAlign: 'left'
    },
    '.cm-gutters': {
      border: '0'
    }
  });

  const initialDoc = buildSqlTemplate('');
  state.lastTemplate = initialDoc;
  const completionKeymap = keymap.of([
    {
      key: 'Tab',
      run: view => {
        if (completionStatus(view.state) !== 'active') {
          return false;
        }
        return acceptCompletion(view);
      }
    }
  ]);
  state.editor = new EditorView({
    doc: initialDoc,
    extensions: [
      completionKeymap,
      oneDark,
      editorTheme,
      editorLanguageCompartment.of(languageExtensionForMode('sql')),
      editorAutocompleteCompartment.of(
        autocompletion({
          override: [completionSource],
          activateOnTyping: true
        })
      ),
      editorPlaceholderCompartment.of(cmPlaceholder('Write SQL here')),
      lintGutter(),
      editorLintCompartment.of(linter(sqlLintSource, { delay: 400 })),
      EditorView.updateListener.of(update => {
        if (update.docChanged) {
          state.visualLoaded = false;
          window.requestAnimationFrame(() => {
            validateEditorContent();
          });
        }
      })
    ],
    parent: elements.editor
  });
}

async function initializeWorkbench() {
  root = typeof document !== 'undefined' ? document.getElementById('sql-workbench-page') : null;
  if (!root) {
    return;
  }
  elements.authPanel = document.getElementById('sqlwb-auth-panel');
  elements.app = document.getElementById('sqlwb-app');
  elements.status = document.getElementById('sqlwb-status');
  elements.editor = document.getElementById('sqlwb-editor');
  elements.editorContext = document.getElementById('sqlwb-editor-context');
  elements.sourceSelect = document.getElementById('sqlwb-source-select');
  elements.openConnectionButton = document.getElementById('sqlwb-open-connection-btn');
  elements.connectionPanel = document.getElementById('sqlwb-connection-panel');
  elements.connectionNameInput = document.getElementById('sqlwb-connection-name');
  elements.connectionTypeSelect = document.getElementById('sqlwb-connection-type');
  elements.connectionInputModeSelect = document.getElementById('sqlwb-connection-input-mode');
  elements.connectionStringField = document.getElementById('sqlwb-connection-string-field');
  elements.connectionStringInput = document.getElementById('sqlwb-connection-string');
  elements.connectionHostField = document.getElementById('sqlwb-connection-host-field');
  elements.connectionHostInput = document.getElementById('sqlwb-connection-host');
  elements.connectionPortField = document.getElementById('sqlwb-connection-port-field');
  elements.connectionPortInput = document.getElementById('sqlwb-connection-port');
  elements.connectionDatabaseField = document.getElementById('sqlwb-connection-database-field');
  elements.connectionDatabaseInput = document.getElementById('sqlwb-connection-database');
  elements.connectionSchemaField = document.getElementById('sqlwb-connection-schema-field');
  elements.connectionSchemaInput = document.getElementById('sqlwb-connection-schema');
  elements.connectionUsernameField = document.getElementById('sqlwb-connection-username-field');
  elements.connectionUsernameInput = document.getElementById('sqlwb-connection-username');
  elements.connectionPasswordField = document.getElementById('sqlwb-connection-password-field');
  elements.connectionPasswordInput = document.getElementById('sqlwb-connection-password');
  elements.connectionNote = document.getElementById('sqlwb-connection-note');
  elements.saveConnectionButton = document.getElementById('sqlwb-save-connection-btn');
  elements.cancelConnectionButton = document.getElementById('sqlwb-cancel-connection-btn');
  elements.entitySelect = document.getElementById('sqlwb-entity-select');
  elements.entityTree = document.getElementById('sqlwb-entity-tree');
  elements.entityFilter = document.getElementById('sqlwb-entity-filter');
  elements.treeExpandAll = document.getElementById('sqlwb-tree-expand-all');
  elements.treeCollapseAll = document.getElementById('sqlwb-tree-collapse-all');
  elements.aiToggle = document.getElementById('sqlwb-ai-toggle');
  elements.aiRow = document.getElementById('sqlwb-ai-row');
  elements.modeSelect = document.getElementById('sqlwb-mode-select');
  elements.titleInput = document.getElementById('sqlwb-title');
  elements.aiPrompt = document.getElementById('sqlwb-ai-prompt');
  elements.visualPrompt = document.getElementById('sqlwb-visual-prompt');
  elements.runButton = document.getElementById('sqlwb-run-btn');
  elements.aiCompleteButton = document.getElementById('sqlwb-ai-complete-btn');
  elements.saveButton = document.getElementById('sqlwb-save-btn');
  elements.newButton = document.getElementById('sqlwb-new-btn');
  elements.shareButton = document.getElementById('sqlwb-share-btn');
  elements.generateButton = document.getElementById('sqlwb-generate-btn');
  elements.visualizeButton = document.getElementById('sqlwb-visualize-btn');
  elements.editorDiagnostics = document.getElementById('sqlwb-editor-diagnostics');
  elements.resultsMeta = document.getElementById('sqlwb-results-meta');
  elements.resultsContent = document.getElementById('sqlwb-results-content');
  elements.profileMeta = document.getElementById('sqlwb-profile-meta');
  elements.profileOutput = document.getElementById('sqlwb-profile-output');
  elements.visualMeta = document.getElementById('sqlwb-visual-meta');
  elements.visualOutput = document.getElementById('sqlwb-visual-output');
  elements.schemaOutput = document.getElementById('sqlwb-schema-output');
  elements.savedList = document.getElementById('sqlwb-saved-list');
  elements.snippetSearch = document.getElementById('sqlwb-snippet-search');
  elements.capabilities = document.getElementById('sqlwb-capabilities');
  elements.chatPanel = document.getElementById('sqlwb-chat-panel');
  elements.chatMessages = document.getElementById('sqlwb-chat-messages');
  elements.chatInput = document.getElementById('sqlwb-chat-input');
  elements.chatSendBtn = document.getElementById('sqlwb-chat-send-btn');
  elements.chatCloseBtn = document.getElementById('sqlwb-chat-close-btn');
  elements.chatToggleBtn = document.getElementById('sqlwb-chat-toggle-btn');

  elements.erdDialog = document.getElementById('sqlwb-erd-dialog');
  elements.erdTitle = document.getElementById('sqlwb-erd-title');
  elements.erdLoading = document.getElementById('sqlwb-erd-loading');
  elements.erdError = document.getElementById('sqlwb-erd-error');
  elements.erdNotice = document.getElementById('sqlwb-erd-notice');
  elements.erdHint = document.getElementById('sqlwb-erd-hint');
  elements.erdSvg = document.getElementById('sqlwb-erd-svg');
  elements.erdLegend = document.getElementById('sqlwb-erd-legend');
  elements.erdResetZoom = document.getElementById('sqlwb-erd-reset-zoom');

  state.secret = getBootstrapSecret();
  if (!state.secret) {
    elements.authPanel.hidden = false;
    return;
  }

  // Hook into data-connectors.js to reload sources after a new connection is saved
  if (window.DataConnectors) {
    window.DataConnectors.onSaved = (ds) => {
      loadDataSources(ds && ds.id ? ds.id : '').catch(() => {});
    };
  }

  elements.app.hidden = false;
  syncModeOptions('sql');
  initializeEditor();
  updateConnectionForm();
  initContextMenu();
  bindEvents();
  restoreAiRowState();
  bindTreeContextMenuBridge();
  renderSavedDrafts();
  loadSnippets().catch(() => {});
  loadCapabilities();
  validateEditorContent();

  const urlParams = new URLSearchParams(window.location.search);
  const preferredSourceId = urlParams.get('data_source_id') || '';
  const preferredEntityName = urlParams.get('entity_name') || urlParams.get('table') || '';
  const sharedId = urlParams.get('shared') || '';

  const draft = getStoredJson(STORAGE_KEYS.draft, null);
  if (!preferredSourceId && !sharedId && draft && typeof draft === 'object') {
    elements.titleInput.value = draft.title || '';
    elements.aiPrompt.value = draft.aiPrompt || '';
    elements.visualPrompt.value = draft.visualPrompt || '';
  }

  try {
    await loadDataSources(
      preferredSourceId || (!sharedId && draft && draft.sourceId) || '',
      preferredEntityName || (!sharedId && draft && draft.entityName) || ''
    );
    if (sharedId) {
      await loadSharedQuery(sharedId);
    } else if (draft && !preferredSourceId && draft.query && state.dataSources.length) {
      restoreDraft(draft);
    } else if (draft && draft.query && !state.dataSources.length) {
      setEditorText(draft.query, false);
    } else if (!elements.titleInput.value.trim()) {
      elements.titleInput.value = titleFromState();
    }

    if (state.dataSources.length) {
      clearStatus();
    }
  } catch (error) {
    showStatus(error.message || 'Failed to initialize the workbench.', 'error');
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWorkbench);
  } else {
    initializeWorkbench();
  }
}

// Always expose a minimal API so inline template scripts can interact with the workbench.
if (typeof window !== 'undefined') {
  window.sqlWbAPI = {
    share: shareWorkbench,
    loadShared: loadSharedQuery,
    getQuery: editorText,
    getCurrentShareId: () => state.currentShareId,
    addEntityToDashboard,
    fetchSuggestedQuery,
  };
}

// Expose pure functions for in-browser Jasmine testing when test mode is active.
if (typeof window !== 'undefined' && window.__TEST_MODE__) {
  window.sqlWbTestHooks = {
    sourceModeForType,
    safeEntityLabel,
    connectionTypeConfig,
    buildSqlTemplate,
    buildMongoTemplate,
    mongoCollectionReference,
    buildElasticTemplate,
    buildTemplateForMode,
    validateConnectionPayload,
    getStoredJson,
    setStoredJson,
    getSavedDrafts,
    getBootstrapSecret,
    apiUrl,
  };
}
